import type {
  AudioEvent,
  ProclaimKaraokeDriver,
} from "../../../shared/audio/events";
import { resolvePlayableDeck } from "../../../shared/deck/storage";
import { confusionClaim, lieById } from "../data/lies";
import {
  TAKE_EVERY_THOUGHT,
  type ScenarioDef,
} from "../data/scenario";
import {
  strikeQuality,
  type BoardFxEvent,
} from "./boardFx";
import {
  checkpointIsStable,
  type EndlessCheckpointPayload,
} from "./endlessCheckpoint";
import {
  createEscalationRuntime,
  currentStrategy,
  effectiveInterval,
  layerOf,
  maybeAdvanceStrategy,
  pickWeighted,
  scriptEndTime,
  type EscalationRuntime,
} from "./escalation";
import {
  STARTER_DECK_IDS,
  WISDOM_VERSE_IDS,
  proclaimLine,
  speakDuration,
  verseById,
} from "../data/verses";
import {
  allLies,
  canPlaceInSlot,
  clampTilt,
  countOnBoard,
  countSide,
  createEmptyBoard,
  findPresence,
  forEachPresence,
  isUncontested,
  layerHasSide,
  layerInward,
  layerOutward,
  layerWithMostPrayers,
  totalPresences,
  type BoardState,
} from "./board/board";
import {
  LAYER_LABELS,
  LAYER_ORDER,
  SLOT_COUNT,
  slotsAdjacent,
  type Category,
  type Difficulty,
  type LayerId,
  type LieType,
} from "./board/types";
import { tiltBand } from "./board/tiltBands";
import {
  applyDogmaHit,
  applyDogmaNameThenMaybePeel,
  dogmaArtLevel,
  dogmaClaim,
  dogmaDemand,
  dogmaIsCore,
  dogmaMeta,
  dogmaName,
  dogmaRemaining,
  initDogmaState,
  isDogmaPresence,
  type DogmaScaffold,
} from "./combat/dogma";
import {
  applyDoubtDecay,
  applyDoubtHit,
  convincesDoubt,
  DOUBT_DECAY_SECONDS,
  doubtCoreExposed,
  doubtFaithProgress,
  doubtHitStrength,
  doubtRemaining,
  initDoubtState,
  isDoubtPresence,
  type DoubtHitKind,
  type DoubtScaffold,
} from "./combat/doubt";
import {
  gatherCapacity,
  planGather,
  type PrayerRef,
} from "./combat/gather";
import { isLieRedeemable } from "./combat/redeemable";
import {
  answerStepCount,
  correctTargetLabel,
  isCorrectSelectorTarget,
  meetsLie,
} from "./combat/matchups";
import type {
  CardInstance,
  DragIntent,
  LieDef,
  LieSpawnSkin,
  Presence,
  TouchAttribution,
  VerseDef,
} from "./cards/types";
import {
  isEmpowerableInstant,
  levelRoman,
  lieMaxLevel,
  lieStageAt,
} from "./cards/types";
import {
  classifyLogMessage,
  emptyRunStats,
  type CombatLogEntry,
  type LogKind,
  type LogOrigin,
  type RunStats,
} from "./combatLog";
import {
  createResources,
  type ResourceState,
} from "./resources";

export type GameStatus = "playing" | "won" | "lost";
export type { CombatLogEntry, LogOrigin, RunStats } from "./combatLog";

export type DogmaNotice = {
  demand: Category;
  wisdomInDeck: boolean;
  remaining: number;
  core: boolean;
  named: boolean;
  sequence: Category[];
  scaffold: "teach" | "full";
};

export interface GameSnapshot {
  status: GameStatus;
  resources: ResourceState;
  hand: CardInstance[];
  board: BoardState;
  elapsed: number;
  duration: number;
  /** Free-play endless: timer counts up; no duration win */
  endless: boolean;
  waveLabel: string;
  lastMessage: string;
  paused: boolean;
  difficulty: Difficulty;
  pendingRevealId: string | null;
  combatLogLength: number;
  /** Seconds remaining of current proclaim (0 = ready) */
  proclaimLock: number;
  proclaimLockMax: number;
  proclaimRef: string;
  proclaimText: string;
  /** Karaoke: how many words are “spoken” so far */
  proclaimWordIndex: number;
  proclaimWordCount: number;
  /** Verse being proclaimed (empty when not locked) */
  proclaimVerseId: string;
  /** Draw pile remaining (not yet in hand) */
  drawPileSize: number;
  /** Used / discard pile size */
  discardPileSize: number;
  /** Full run deck size (draw + hand + discard) */
  deckTotalSize: number;
  /** Settled on the board — hunt strip / lane highlight. */
  settledHunt: SettledHunt | null;
}

export type SettledHunt = {
  name: string;
  claim: string;
  layer: LayerId;
  slot: number;
  demand: Category;
  named: boolean;
  remaining: number;
  total: number;
  onWill: boolean;
};

let presenceSeq = 1;
let cardSeq = 1;

function parseSeqId(id: string, prefix: string): number {
  if (!id.startsWith(prefix)) return 0;
  const n = Number(id.slice(prefix.length));
  return Number.isFinite(n) ? n : 0;
}

/** Deep-ish clone of board + presences for checkpoint storage. */
function cloneBoardState(board: BoardState): BoardState {
  const out = createEmptyBoard();
  for (const id of LAYER_ORDER) {
    const src = board[id];
    if (!src) continue;
    out[id]!.tilt = src.tilt;
    out[id]!.flash = 0;
    out[id]!.slots = src.slots.map((p) => {
      if (!p) return null;
      // structuredClone keeps Presence plain data (incl. doubt)
      try {
        return structuredClone(p);
      } catch {
        return JSON.parse(JSON.stringify(p)) as typeof p;
      }
    });
  }
  return out;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Must settle this long on a layer after spawn/move before advancing again. */
const ADVANCE_DWELL = 2.1;

/** Seconds of equal-level contest ≈ one mutual step (wear fills at this rate scale). */
const WEAR_BASE = 0.2;

/**
 * Endless: once this many Prayers are on the board, every protocol spawn
 * hunts the wall instead of dying in leftover gaps.
 */
const PRAYER_WALL_COUNT = 5;

const TRUTH_FALLBACK: Record<
  LieType,
  { reference: string; line: string; defId: string }
> = {
  fear: {
    reference: "Psalm 56:3",
    line: "What time I am afraid, I will trust in thee.",
    defId: "ps-56-3",
  },
  despair: {
    reference: "Nehemiah 8:10",
    line: "…the joy of the Lord is your strength.",
    defId: "neh-8-10",
  },
  confusion: {
    reference: "2 Corinthians 10:5",
    line: "…bringing into captivity every thought to the obedience of Christ.",
    defId: "2cor-10-5",
  },
  doubt: {
    reference: "Mark 11:22",
    line: "Have faith in God.",
    defId: "mark-11-22",
  },
  dogma: {
    reference: "James 1:5",
    line: "If any of you lack wisdom, let him ask of God…",
    defId: "jas-1-5",
  },
};

/** Seconds a residual (I) lie may linger before twisting into Confusion (II). */
const CONFUSE_AFTER = 7.5;

function shortScripture(text: string, max = 88): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trim()}…`;
}

function verseTouch(def: VerseDef): TouchAttribution {
  return {
    kind: "verse",
    defId: def.id,
    reference: def.reference,
    line: shortScripture(def.textKjv),
  };
}

function prayerTouch(p: Presence): TouchAttribution {
  const def = verseById(p.defId);
  return {
    kind: "prayer",
    defId: p.defId,
    reference: def?.reference ?? p.name,
    line: def ? shortScripture(def.textKjv) : p.claim,
  };
}

function prayerClaimLine(def: VerseDef): string {
  return shortScripture(def.textKjv, 72);
}

function intensityKey(level: number): 1 | 2 | 3 {
  const n = Math.round(level);
  if (n >= 3) return 3;
  if (n <= 1) return 1;
  return 2;
}

/** Resolve themed claim / name for this intensity (campaign skins). */
function skinnedClaim(p: Presence, level: number): string | undefined {
  const skin = p.claimSkin;
  if (!skin) return undefined;
  const k = intensityKey(level);
  return skin[k] ?? skin[3] ?? skin[2] ?? skin[1];
}

function skinnedName(p: Presence, level: number): string | undefined {
  const skin = p.nameSkin;
  if (!skin) return undefined;
  const k = intensityKey(level);
  return skin[k] ?? skin[3] ?? skin[2] ?? skin[1];
}

/** Player Answer / Chip / Soften / Still dumps a Secret Place accusation. */
function rewindAccusation(p: Presence): boolean {
  if (!p.verbs.taxAfter && p.taxProgress <= 0 && p.taxTimer <= 0) return false;
  const hadCharge = p.taxProgress > 0.08 || p.taxTimer > 0.4;
  p.taxTimer = 0;
  p.taxProgress = 0;
  return hadCharge;
}

function applyLieStage(p: Presence, def: LieDef, level: number): void {
  const stage = lieStageAt(def, level);
  p.level = level;
  p.name = skinnedName(p, level) ?? stage.name ?? def.name;
  p.claim = skinnedClaim(p, level) ?? stage.claim;
  p.meta = `${def.type} · ${levelRoman(level)}`;
  p.verbs = { ...stage.verbs };
  p.wear = 0;
  if (!p.verbs.taxAfter) {
    p.taxTimer = 0;
    p.taxProgress = 0;
  }
  // Confusion keeps a twisted claim rooted in the source residual lie
  if (def.id === "confusion" && p.sourceLieId) {
    const residual =
      skinnedClaim(p, 1) ?? skinnedClaim(p, 2) ?? skinnedClaim(p, 3);
    if (residual) {
      p.claim =
        level >= 2
          ? `It almost sounds wise: “${residual}” — until you look straight at it.`
          : `A tangled echo remains: “${residual}”`;
    } else {
      p.claim = confusionClaim(p.sourceLieId, level);
    }
    p.name =
      skinnedName(p, level) ??
      (level >= 2 ? "Confusion" : "Tangled Echo");
    p.meta = `confusion · ${levelRoman(level)}`;
  }
  // Doubt: show conviction progress when onion state is present
  if (p.doubt && def.type === "doubt") {
    const { faith, total } = doubtFaithProgress(p.doubt);
    if (p.doubt.outerCount === 0) {
      p.meta = `doubt · core open · Redeem ready`;
    } else {
      p.meta = `doubt · ${faith}/${total} Faith`;
      if (doubtCoreExposed(p.doubt)) {
        p.meta += " · core open";
      }
      if (!p.doubt.decayEnabled) {
        p.meta += " · no timer";
      }
    }
  }
  // Marching copies always know how to push if stage lost advance
  if (p.marching && !p.verbs.advanceAfter) {
    p.verbs = { ...p.verbs, advanceAfter: 2.8 };
  }
  if (p.dogma && def.type === "dogma") {
    const taxAfter = p.verbs.taxAfter;
    const taxHit = p.verbs.taxHit;
    const stage = lieStageAt(def, dogmaArtLevel(p.dogma));
    p.verbs = { ...stage.verbs };
    if (taxAfter) {
      p.verbs = { ...p.verbs, taxAfter, taxHit };
    }
    syncDogmaFace(p);
  }
}

function applySpawnSkin(
  p: Presence,
  skin: LieSpawnSkin | undefined,
): void {
  if (!skin) return;
  const claims: Partial<Record<1 | 2 | 3, string>> = {
    ...(skin.claims ?? {}),
  };
  if (skin.claim && !claims[3]) claims[3] = skin.claim;
  if (Object.keys(claims).length) p.claimSkin = claims;

  const names: Partial<Record<1 | 2 | 3, string>> = {
    ...(skin.names ?? {}),
  };
  if (skin.name && !names[3]) names[3] = skin.name;
  if (Object.keys(names).length) p.nameSkin = names;

  if (skin.artId) p.artId = skin.artId;
  if (skin.voId) p.voId = skin.voId;
  else if (skin.artId) p.voId = skin.artId;
}

function makePlayerPresence(def: VerseDef, homeSlot = 1): Presence {
  const pl = def.placeable!;
  const level = Math.max(1, Math.min(3, pl.level));
  const advances = !!pl.verbs.advanceAfter;
  return {
    instanceId: `p-${presenceSeq++}`,
    side: "player",
    defId: def.id,
    name: pl.name,
    meta: `${def.category} · ${levelRoman(level)}`,
    level,
    maxLevel: level,
    wear: 0,
    claim: prayerClaimLine(def),
    age: 0,
    advanceTimer: 0,
    spreadTimer: 0,
    // Advancing prayers settle briefly before walking the path
    dwell: advances ? ADVANCE_DWELL * 0.85 : 0,
    advanceProgress: 0,
    taxTimer: 0,
    taxProgress: 0,
    category: def.category,
    verbs: { ...pl.verbs },
    spreadGen: 0,
    homeSlot,
    marching: false,
    anim: "spawn",
    animTime: 0.35,
    slowTimer: 0,
    slowFactor: 1,
    fortifyTimer: 0,
    fortifyWearFactor: 1,
  };
}

const REDEEM_MAX_LEVEL = 3;

/** Re-export pure helper (UI / bots may import from Game historically). */
export { isLieRedeemable };

/**
 * Prayer intensity after Redeem (relative to the lie):
 * - bonus 0 (weak): same level as the lie
 * - bonus 1 (strong): one step above when possible; III lie → III fortified
 */
export function redeemPrayerLevel(
  def: VerseDef,
  lieLevel: number,
): { level: number; elite: boolean } {
  const lie = Math.max(1, Math.min(REDEEM_MAX_LEVEL, Math.round(lieLevel)));
  const bonus = Math.max(0, def.convertPrayerBonus ?? 0);
  const level = Math.min(
    REDEEM_MAX_LEVEL,
    Math.max(1, lie + bonus),
  );
  // Fortified only when strong redeem hits the cap (wanted lie+1 but max is 3)
  const elite = bonus >= 1 && lie >= REDEEM_MAX_LEVEL;
  return { level, elite };
}

function redeemPrayerVerbs(
  level: number,
  elite: boolean,
): Presence["verbs"] {
  // Scale Truth pressure / contest with level; elite (from a III lie) is sturdier
  const basePressure = -5.5 - level * 1.2; // II ≈ -7.9, III ≈ -9.1
  const baseContest = 1.1 + level * 0.35;
  if (elite) {
    return {
      pressure: basePressure - 1.5,
      guard: true,
      contest: baseContest + 0.6,
    };
  }
  return {
    pressure: basePressure,
    guard: true,
    contest: baseContest,
  };
}

function makeRedeemPrayer(
  def: VerseDef,
  homeSlot: number,
  name: string,
  level: number,
  elite: boolean,
): Presence {
  const lv = Math.max(1, Math.min(REDEEM_MAX_LEVEL, level));
  return {
    instanceId: `p-${presenceSeq++}`,
    side: "player",
    defId: def.id,
    name,
    meta:
      `${def.category} · Redeemed · ${levelRoman(lv)}` +
      (elite ? " · fortified" : ""),
    level: lv,
    maxLevel: lv,
    wear: 0,
    claim: shortScripture(def.textKjv, 72),
    age: 0,
    advanceTimer: 0,
    spreadTimer: 0,
    dwell: 0,
    advanceProgress: 0,
    taxTimer: 0,
    taxProgress: 0,
    category: def.category,
    verbs: redeemPrayerVerbs(lv, elite),
    spreadGen: 0,
    homeSlot,
    marching: false,
    anim: "spawn",
    animTime: 0.5,
    slowTimer: 0,
    slowFactor: 1,
    fortifyTimer: 0,
    fortifyWearFactor: 1,
  };
}

function makeLiePresence(
  def: LieDef,
  spreadGen = 0,
  opts?: {
    marching?: boolean;
    anim?: Presence["anim"];
    homeSlot?: number;
    /** Override starting level (default max; spawnlings often max-1) */
    level?: number;
    /** Campaign / scenario narrative skin */
    skin?: LieSpawnSkin;
    /** Inherit skins from a parent (multiply) */
    inheritFrom?: Presence;
    /** Doubt only: core / half / full onion (default full) */
    doubtScaffold?: DoubtScaffold;
    /** Dogma only: teach (3) / full (4) */
    dogmaScaffold?: DogmaScaffold;
  },
): Presence {
  const maxLv = lieMaxLevel(def);
  let level = opts?.level ?? maxLv;
  if (spreadGen > 0 && opts?.level === undefined) {
    level = Math.max(1, maxLv - 1);
  }
  level = Math.max(1, Math.min(maxLv, level));

  const p: Presence = {
    instanceId: `e-${presenceSeq++}`,
    side: "enemy",
    defId: def.id,
    name: def.name,
    meta: `${def.type}`,
    level,
    maxLevel: maxLv,
    wear: 0,
    claim: "",
    age: 0,
    advanceTimer: 0,
    spreadTimer: 0,
    dwell: opts?.marching ? ADVANCE_DWELL * 0.85 : ADVANCE_DWELL,
    advanceProgress: 0,
    taxTimer: 0,
    taxProgress: 0,
    lieType: def.type,
    verbs: {},
    spreadGen,
    homeSlot: opts?.homeSlot ?? 1,
    marching: opts?.marching ?? false,
    anim: opts?.anim ?? "spawn",
    animTime: 0.4,
    slowTimer: 0,
    slowFactor: 1,
    fortifyTimer: 0,
    fortifyWearFactor: 1,
  };

  if (opts?.inheritFrom) {
    const parent = opts.inheritFrom;
    if (parent.claimSkin) p.claimSkin = { ...parent.claimSkin };
    if (parent.nameSkin) p.nameSkin = { ...parent.nameSkin };
    if (parent.artId) p.artId = parent.artId;
    if (parent.voId) p.voId = parent.voId;
  }
  applySpawnSkin(p, opts?.skin);
  if (def.type === "doubt") {
    const scaffold = opts?.doubtScaffold ?? "full";
    p.doubt = initDoubtState(maxLv, scaffold);
    // Spawnlings copy parent's shell count but reset Faith progress
    if (opts?.inheritFrom?.doubt) {
      const parentD = opts.inheritFrom.doubt;
      p.doubt = {
        outerCount: parentD.outerCount,
        maxOuterCount: parentD.maxOuterCount,
        outerFaith: 0,
        decayAcc: 0,
        decayEnabled: parentD.decayEnabled,
      };
    }
    level = doubtRemaining(p.doubt);
  }
  if (def.type === "dogma") {
    p.dogma = initDogmaState(opts?.dogmaScaffold ?? "teach");
    applyLieStage(p, def, dogmaArtLevel(p.dogma));
    syncDogmaFace(p);
    return p;
  }
  applyLieStage(p, def, level);
  return p;
}

function syncDogmaFace(p: Presence): void {
  if (!p.dogma) return;
  p.level = dogmaArtLevel(p.dogma);
  p.maxLevel = 3;
  p.name = dogmaName(p.dogma);
  p.claim = dogmaClaim(p.dogma);
  p.meta = dogmaMeta(p.dogma);
  p.wear = 0;
}

/**
 * How hard a presence wears foes on its layer.
 * Lies without an explicit contest verb still strain a keep via pressure.
 */
function effectiveContest(p: Presence): number {
  if (p.verbs.contest && p.verbs.contest > 0) return p.verbs.contest;
  if (p.side === "enemy" && p.verbs.pressure && p.verbs.pressure > 0) {
    return Math.max(1.0, p.verbs.pressure * 0.25);
  }
  if (p.side === "player" && p.verbs.pressure && p.verbs.pressure < 0) {
    // Pure Truth-pressure prayers lightly contest by abiding
    return Math.max(0.8, Math.abs(p.verbs.pressure) * 0.2);
  }
  return 0;
}

/** Wear rate toward one step-down of the defender. */
function wearRate(attacker: Presence, defender: Presence): number {
  const contest = effectiveContest(attacker);
  if (contest <= 0) return 0;
  const base = contest * WEAR_BASE * 0.55;
  const diff = attacker.level - defender.level;
  // Equal ≈ fair mutual cancel; higher level disrupts faster.
  let mul = diff === 0 ? 1 : diff > 0 ? 1 + diff * 0.55 : Math.max(0.22, 1 + diff * 0.45);
  if (defender.verbs.guard) mul *= 0.62;
  if (attacker.side === "player" && attacker.category) {
    if (meetsLie(attacker.category, defender.lieType)) mul *= 1.2;
  }
  return base * mul;
}

const BOARD_CAP = 9;

type PendingStep = {
  presence: Presence;
  layer: LayerId;
  slot: number;
  fromLevel: number;
  toLevel: number;
};

export class Game {
  scenario: ScenarioDef;
  difficulty: Difficulty = "standard";
  status: GameStatus = "playing";
  elapsed = 0;
  resources!: ResourceState;
  hand: CardInstance[] = [];
  /**
   * Draw pile (face-down). Pop to draw.
   * When empty, `discardPile` is shuffled back in.
   */
  deck: string[] = [];
  /** Used pile — played cards land here until reshuffle. */
  discardPile: string[] = [];
  /**
   * Full run deck composition (with multiplicity).
   * From shared deck picker or campaign loadout — not reshuffled mid-run.
   */
  deckPool: string[] = [...STARTER_DECK_IDS];
  board!: BoardState;
  waveLabel = "Prepare";
  lastMessage = "";
  /** Speech resource: cannot start another verse while > 0 */
  proclaimLock = 0;
  proclaimLockMax = 0;
  proclaimRef = "";
  proclaimText = "";
  proclaimWordIndex = 0;
  proclaimWordCount = 0;
  /** Def id of the verse currently being spoken (cleared with lock). */
  proclaimVerseId = "";
  /**
   * Empower charges remaining — next Answer/Chip spends one for +bonus steps.
   * Eligible hand cards are highlighted in HandUi while charges > 0.
   */
  empowerCharges = 0;
  /** Extra Answer/Chip levels per Empower charge (usually 1). */
  empowerBonusSteps = 1;
  /** Next proclaim durations multiplied by this (Pray without ceasing, etc.). */
  private speakHasteCharges = 0;
  private speakHasteFactor = 1;
  /**
   * Nehemiah 8:10-style Joy: board-wide soft contest on matching lie types.
   * Cleared on reset / when remaining hits 0.
   */
  private joyField: {
    remaining: number;
    wearPerSec: number;
    onlyTypes: LieType[];
    slowFactor: number;
    maxStepsPerLie: number;
    /** Steps already dealt this field cast, by presence instance id */
    stepsDone: Map<string, number>;
    reference: string;
    defId: string;
  } | null = null;
  /** Full mission log with timestamps (debug + end summary). */
  combatLog: CombatLogEntry[] = [];
  stats!: RunStats;
  /** True when sim is frozen (user pause and/or Easy Reveal). */
  paused = false;
  /** Explicit pause from button / Space — independent of Reveal. */
  userPaused = false;
  pendingRevealId: string | null = null;
  /**
   * When set, `reset` uses this deck instead of the shared deck picker.
   * Campaign missions set this; free play leaves it null.
   */
  private forcedDeckPool: string[] | null = null;
  private firedSpawns = new Set<number>();
  private escalation: EscalationRuntime = createEscalationRuntime();
  private logListeners: Array<(msg: string, entry: CombatLogEntry) => void> =
    [];
  private audioListeners: Array<(e: AudioEvent) => void> = [];
  private boardFxListeners: Array<(e: BoardFxEvent) => void> = [];
  private checkpointListeners: Array<(p: EndlessCheckpointPayload) => void> =
    [];
  private protocolBoundaryListeners: Array<
    (info: {
      waveLabel: string;
      strategyIndex: number;
      cycles: number;
      isFirst: boolean;
    }) => void
  > = [];
  /** Optional pure karaoke clock (set from main/engine — never AudioContext). */
  private karaokeDriver: ProclaimKaraokeDriver | null = null;
  /** Last protocol key we emitted a checkpoint for (avoid spam within a phase). */
  private lastCheckpointKey = "";
  /**
   * Muster climb: verse ids added mid-run (field findings).
   * Cleared on reset; used for end-of-run inscribe candidates.
   */
  findingsThisRun: string[] = [];
  private logSeq = 0;
  /**
   * First Settled spawn this run — UI gifts James 1:5 / coaches the peel.
   * Consumed by main (collection lives outside Game).
   */
  private dogmaNotice: DogmaNotice | null = null;
  private dogmaNoticed = false;
  /** Prevent repeat win/lose meta lines every frame */
  private endLogged = false;
  /** Layers that already announced a packed-row crowd this stretch. */
  private crowdLogged = new Set<LayerId>();
  /** Coalesce mutual step logs in one frame */
  private pendingSteps: PendingStep[] = [];


  isEndless(): boolean {
    return this.scenario.mode === "endless";
  }

  consumeDogmaNotice(): DogmaNotice | null {
    const n = this.dogmaNotice;
    this.dogmaNotice = null;
    return n;
  }

  /** Multiplier on escalation spawn intervals (higher = gentler). */
  private escalationIntervalMul(): number {
    if (this.difficulty === "easy") return 1.28;
    if (this.difficulty === "hard") return 0.78;
    return 1;
  }

  /**
   * Enemy march / spread pace by difficulty.
   * Hard pushes unanswered lies inward faster so sparse Mind stages still
   * threaten Heart/Will if the player single-files slowly.
   */
  private enemyPaceMul(): number {
    if (this.difficulty === "easy") return 0.88;
    if (this.difficulty === "hard") return 1.18;
    return 1;
  }

  /** Open-Will integrity drain multiplier. */
  private willDpsMul(): number {
    if (this.difficulty === "easy") return 0.85;
    if (this.difficulty === "hard") return 1.12;
    return 1;
  }

  constructor(scenario: ScenarioDef = TAKE_EVERY_THOUGHT) {
    this.scenario = scenario;
    this.reset("standard");
  }

  /**
   * Configure scenario + optional fixed loadout, then reset.
   * Pass `deckPool: null` to restore free-play deck resolution.
   */
  configureRun(opts: {
    difficulty: Difficulty;
    scenario?: ScenarioDef;
    deckPool?: string[] | null;
  }): void {
    if (opts.scenario) this.scenario = opts.scenario;
    if (opts.deckPool !== undefined) {
      this.forcedDeckPool =
        opts.deckPool === null ? null : [...opts.deckPool];
    }
    this.reset(opts.difficulty);
  }

  onMessage(fn: (msg: string, entry: CombatLogEntry) => void): void {
    this.logListeners.push(fn);
  }

  /**
   * Structured audio cues. Headless sim never registers listeners.
   * Main wires: `game.onAudioEvent((e) => engine.emit(e))`.
   */
  onAudioEvent(fn: (e: AudioEvent) => void): void {
    this.audioListeners.push(fn);
  }

  /**
   * Short-lived board VFX (strikes, silence, redeem, …). Headless ignores.
   * Main wires: `game.onBoardFx((e) => boardUi.playFx(e))`.
   */
  onBoardFx(fn: (e: BoardFxEvent) => void): void {
    this.boardFxListeners.push(fn);
  }

  /**
   * Free-play: stable protocol-boundary combat snapshots for resume.
   * Main wires persistence; Game only builds the payload.
   */
  onEndlessCheckpoint(fn: (p: EndlessCheckpointPayload) => void): void {
    this.checkpointListeners.push(fn);
  }

  /** Protocol start / advance — muster findings and wave UI. */
  onProtocolBoundary(
    fn: (info: {
      waveLabel: string;
      strategyIndex: number;
      cycles: number;
      isFirst: boolean;
    }) => void,
  ): void {
    this.protocolBoundaryListeners.push(fn);
  }

  /** Pure karaoke clock from audio engine; null restores linear lock timing. */
  setProclaimKaraokeDriver(driver: ProclaimKaraokeDriver | null): void {
    this.karaokeDriver = driver;
  }

  isMusterScenario(): boolean {
    return this.scenario.id === "endless-muster";
  }

  /**
   * Muster finding: add verse to field deck on top of the draw pile
   * (next `pop` draws it). Also records for end-of-run inscribe list.
   */
  addFieldFinding(verseId: string): boolean {
    if (this.status !== "playing") return false;
    const def = verseById(verseId);
    if (!def) return false;
    this.deckPool.push(verseId);
    // draw uses pop() — end of array is next drawn
    this.deck.push(verseId);
    this.findingsThisRun.push(verseId);
    this.stats.deckPoolSize = this.deckPool.length;
    this.log(
      `Provision · ${def.reference} rests on top of the draw pile.`,
      "system",
    );
    return true;
  }

  /**
   * Muster cull: remove one copy of a verse from the field deck.
   * Prefers draw pile, then discard, then hand. Findings history is kept
   * (you still "found" it for end-of-run inscribe).
   */
  removeFieldVerse(verseId: string): boolean {
    if (this.status !== "playing") return false;
    const def = verseById(verseId);
    const poolI = this.deckPool.lastIndexOf(verseId);
    if (poolI < 0) return false;
    this.deckPool.splice(poolI, 1);

    const drawI = this.deck.lastIndexOf(verseId);
    if (drawI >= 0) {
      this.deck.splice(drawI, 1);
    } else {
      const discI = this.discardPile.lastIndexOf(verseId);
      if (discI >= 0) {
        this.discardPile.splice(discI, 1);
      } else {
        const handI = this.hand.findIndex((c) => c.def.id === verseId);
        if (handI >= 0) {
          const [card] = this.hand.splice(handI, 1);
          if (card && this.pendingRevealId === card.instanceId) {
            this.closeReveal();
          }
        }
      }
    }

    this.stats.deckPoolSize = this.deckPool.length;
    const ref = def?.reference ?? verseId;
    this.log(`Provision · laid aside ${ref} from the field deck.`, "system");
    return true;
  }

  /** Field deck size (muster + findings still in the run). */
  fieldDeckSize(): number {
    return this.deckPool.length;
  }

  getFindingsThisRun(): readonly string[] {
    return this.findingsThisRun;
  }

  /**
   * Full restorable combat state for endless free play.
   * Clears speech lock on restore (caller starts ready to play).
   */
  exportEndlessCheckpoint(): EndlessCheckpointPayload | null {
    if (!this.isEndless() || this.status !== "playing") return null;
    if (!checkpointIsStable(this.resources)) return null;
    return {
      v: 1,
      scenarioId: this.scenario.id,
      difficulty: this.difficulty,
      savedAt: new Date().toISOString(),
      elapsed: this.elapsed,
      waveLabel: this.waveLabel,
      resources: {
        integrity: this.resources.integrity,
        maxIntegrity: this.resources.maxIntegrity,
      },
      deckPool: [...this.deckPool],
      deck: [...this.deck],
      discardPile: [...this.discardPile],
      hand: this.hand.map((c) => c.def.id),
      board: cloneBoardState(this.board),
      firedSpawns: [...this.firedSpawns],
      escalation: { ...this.escalation },
      empowerCharges: this.empowerCharges,
      empowerBonusSteps: this.empowerBonusSteps,
      speakHasteCharges: this.speakHasteCharges,
      speakHasteFactor: this.speakHasteFactor,
      joyField: this.joyField
        ? {
            remaining: this.joyField.remaining,
            wearPerSec: this.joyField.wearPerSec,
            onlyTypes: [...this.joyField.onlyTypes],
            slowFactor: this.joyField.slowFactor,
            maxStepsPerLie: this.joyField.maxStepsPerLie,
            stepsDone: [...this.joyField.stepsDone.entries()],
            reference: this.joyField.reference,
            defId: this.joyField.defId,
          }
        : null,
      presenceSeq,
      cardSeq,
      lastMessage: this.lastMessage,
    };
  }

  /**
   * Apply a free-play checkpoint after configureRun/reset.
   * Returns false if payload is unusable (caller should keep fresh run).
   */
  applyEndlessCheckpoint(payload: EndlessCheckpointPayload): boolean {
    if (!payload || payload.v !== 1) return false;
    if (!this.isEndless()) return false;

    // Rebuild hand from def ids
    const hand: CardInstance[] = [];
    for (const id of payload.hand) {
      const def = verseById(id);
      if (!def) continue;
      hand.push({ instanceId: `c-${cardSeq++}`, def, justDrawn: false });
    }
    // Drop unknown verses from piles
    const deck = payload.deck.filter((id) => !!verseById(id));
    const discard = payload.discardPile.filter((id) => !!verseById(id));
    const pool = payload.deckPool.filter((id) => !!verseById(id));
    if (!pool.length) return false;

    this.difficulty = payload.difficulty;
    this.status = "playing";
    this.elapsed = Math.max(0, payload.elapsed);
    this.userPaused = false;
    this.paused = false;
    this.pendingRevealId = null;
    this.firedSpawns = new Set(payload.firedSpawns ?? []);
    this.escalation = {
      ...createEscalationRuntime(),
      ...payload.escalation,
    };
    this.waveLabel = payload.waveLabel || "Resume";
    this.combatLog = [];
    this.logSeq = 0;
    this.endLogged = false;
    this.stats = this.freshStats(payload.difficulty);
    this.stats.elapsed = this.elapsed;
    this.lastMessage = "";
    this.pendingSteps = [];

    this.resources = {
      integrity: Math.min(
        payload.resources.maxIntegrity,
        Math.max(1, payload.resources.integrity),
      ),
      maxIntegrity: payload.resources.maxIntegrity,
    };
    this.stats.minIntegrity = this.resources.integrity;
    this.board = cloneBoardState(payload.board);
    this.forcedDeckPool = [...pool];
    this.deckPool = [...pool];
    this.deck = deck;
    this.discardPile = discard;
    this.hand = hand;
    this.stats.deckPoolSize = this.deckPool.length;

    this.proclaimLock = 0;
    this.proclaimLockMax = 0;
    this.proclaimRef = "";
    this.proclaimText = "";
    this.proclaimWordIndex = 0;
    this.proclaimWordCount = 0;
    this.proclaimVerseId = "";
    this.empowerCharges = Math.max(0, payload.empowerCharges ?? 0);
    this.empowerBonusSteps = Math.max(1, payload.empowerBonusSteps ?? 1);
    this.speakHasteCharges = Math.max(0, payload.speakHasteCharges ?? 0);
    this.speakHasteFactor = payload.speakHasteFactor ?? 1;
    this.joyField = payload.joyField
      ? {
          remaining: payload.joyField.remaining,
          wearPerSec: payload.joyField.wearPerSec,
          onlyTypes: payload.joyField.onlyTypes as LieType[],
          slowFactor: payload.joyField.slowFactor,
          maxStepsPerLie: payload.joyField.maxStepsPerLie,
          stepsDone: new Map(payload.joyField.stepsDone ?? []),
          reference: payload.joyField.reference,
          defId: payload.joyField.defId,
        }
      : null;

    presenceSeq = Math.max(presenceSeq, payload.presenceSeq ?? 1);
    cardSeq = Math.max(cardSeq, payload.cardSeq ?? 1);
    // Ensure unique ids for any board presence vs new cards
    let maxP = presenceSeq;
    forEachPresence(this.board, (p) => {
      const n = parseSeqId(p.instanceId, "p-");
      if (n >= maxP) maxP = n + 1;
    });
    presenceSeq = maxP;

    this.lastCheckpointKey = this.protocolCheckpointKey();
    this.log(
      `Resumed checkpoint · ${this.waveLabel} · ${this.elapsed.toFixed(0)}s · integrity ${Math.round(this.resources.integrity)}`,
      "meta",
    );
    this.log(
      "Full board state restored — same fight, not a soft start. Hold Will.",
      "system",
    );
    this.emitAudio({ type: "mission.start" });
    return true;
  }

  /** Emit a stable protocol checkpoint if listeners care (endless only). */
  private maybeEmitProtocolCheckpoint(force = false): void {
    if (!this.isEndless() || !this.checkpointListeners.length) return;
    if (this.status !== "playing") return;
    if (!checkpointIsStable(this.resources)) return;
    const key = this.protocolCheckpointKey();
    if (!force && key === this.lastCheckpointKey) return;
    const payload = this.exportEndlessCheckpoint();
    if (!payload) return;
    this.lastCheckpointKey = key;
    for (const fn of this.checkpointListeners) {
      try {
        fn(payload);
      } catch {
        /* ignore listener errors */
      }
    }
  }

  private protocolCheckpointKey(): string {
    const e = this.escalation;
    if (!e.active) return `script:${Math.floor(this.elapsed / 30)}`;
    return `p:${e.cycles}:${e.strategyIndex}:${this.waveLabel}`;
  }

  private emitProtocolBoundary(isFirst: boolean): void {
    if (!this.protocolBoundaryListeners.length) return;
    const info = {
      waveLabel: this.waveLabel,
      strategyIndex: this.escalation.strategyIndex,
      cycles: this.escalation.cycles,
      isFirst,
    };
    for (const fn of this.protocolBoundaryListeners) {
      try {
        fn(info);
      } catch {
        /* ignore */
      }
    }
  }

  private emitAudio(e: AudioEvent): void {
    for (const fn of this.audioListeners) fn(e);
  }

  private emitBoardFx(e: BoardFxEvent): void {
    for (const fn of this.boardFxListeners) fn(e);
  }

  /** Lie spawn SFX + claim VO (voId prefers campaign skin claim). */
  private emitLieSpawn(
    p: Presence,
    layer: LayerId,
    opts?: { multiplied?: boolean },
  ): void {
    this.emitAudio({
      type: "lie.spawn",
      lieId: p.defId,
      lieType: p.lieType ?? "fear",
      level: p.level,
      layer,
      multiplied: opts?.multiplied,
      voId: p.voId,
    });
  }

  /**
   * Intensity step-down claim VO (III→II / II→I). Audio layer only plays
   * if no other lie VO is already speaking.
   */
  private emitLieDegrade(p: Presence, layer: LayerId): void {
    this.emitAudio({
      type: "lie.degrade",
      lieId: p.defId,
      lieType: p.lieType ?? "fear",
      level: p.level,
      layer,
      voId: p.voId,
    });
  }

  private meetFields(
    def: VerseDef,
    lieType: LieType | undefined,
  ): boolean {
    const listed =
      !!lieType && !!def.strongVs?.length && def.strongVs.includes(lieType);
    return meetsLie(def.category, lieType) || listed;
  }

  private resolveDogmaHit(
    p: Presence,
    layer: LayerId,
    def: VerseDef,
    opts: {
      kind: "answer" | "nameAnswer" | "chip" | "castDown";
      logAt?: LogOrigin;
    },
  ): void {
    if (!isDogmaPresence(p) || !p.dogma) return;
    const at = opts.logAt ?? this.originOf(layer, p.homeSlot);
    p.lastTouch = verseTouch(def);

    if (opts.kind === "chip") {
      const { result } = applyDogmaHit(p.dogma, {
        kind: "chip",
        category: def.category,
      });
      this.emitBoardFx({
        type: "strike",
        layer,
        slot: p.homeSlot,
        quality: "miss",
        steps: 0,
      });
      this.log(`${def.reference}: ${p.name} — ${result.summary}.`, at);
      return;
    }

    const hit =
      opts.kind === "nameAnswer"
        ? applyDogmaNameThenMaybePeel(p.dogma, def.category)
        : applyDogmaHit(p.dogma, {
            kind: opts.kind === "castDown" ? "castDown" : "answer",
            category: def.category,
          });
    p.dogma = hit.state;

    if (hit.result.silenced) {
      this.silenceDogma(p, layer, def, hit.result.summary, at);
      return;
    }

    const defLie = lieById(p.defId);
    if (defLie) applyLieStage(p, defLie, dogmaArtLevel(p.dogma));
    else syncDogmaFace(p);

    if (hit.result.peeled) {
      // Peel does not reset the walk — a small Still only
      p.advanceProgress *= 0.85;
      p.advanceTimer *= 0.85;
      p.slowTimer = Math.max(p.slowTimer, 2.2);
      p.slowFactor = Math.min(p.slowFactor, 0.45);
    } else if (def.nameLie || opts.kind === "nameAnswer") {
      p.slowTimer = Math.max(p.slowTimer, def.slowDuration ?? 3);
      p.slowFactor = Math.min(p.slowFactor, def.slowFactor ?? 0.4);
      p.advanceProgress *= 0.7;
    }

    if (rewindAccusation(p)) {
      this.log(`${p.name} accusation interrupted.`, at);
    }

    if (hit.result.peeled) {
      this.emitBoardFx({
        type: "degrade",
        layer,
        slot: p.homeSlot,
        side: "enemy",
        fromLevel: p.level + 1,
        toLevel: p.level,
      });
    } else if (hit.result.ground) {
      this.emitBoardFx({
        type: "strike",
        layer,
        slot: p.homeSlot,
        quality: "weak",
        steps: 1,
      });
    } else {
      this.emitBoardFx({
        type: "strike",
        layer,
        slot: p.homeSlot,
        quality: "miss",
        steps: 0,
      });
    }
    this.log(`${def.reference}: ${p.name} — ${hit.result.summary}.`, at);
  }

  private silenceDogma(
    p: Presence,
    layer: LayerId,
    def: VerseDef,
    summary: string,
    at: LogOrigin,
  ): void {
    const was = p.name;
    p.level = 0;
    this.stats.liesSilenced++;
    this.board[layer].tilt = clampTilt(this.board[layer].tilt - 12);
    this.board[layer].flash = 0.55;
    this.log(`${was} falls silent · ${def.reference} · ${summary}`, at);
    this.emitAudio({
      type: "lie.silence",
      lieId: p.instanceId,
      lieType: "dogma",
      layer,
    });
    this.emitBoardFx({
      type: "silence",
      layer,
      slot: p.homeSlot,
      side: "enemy",
    });
    this.cleanupDead();
  }

  private wipeAllConfusion(def: VerseDef, at: LogOrigin): void {
    const hits: Presence[] = [];
    for (const { presence: p, layer, slot } of allLies(this.board)) {
      if (p.lieType !== "confusion" || p.level <= 0) continue;
      p.homeSlot = slot;
      p.level = 0;
      p.lastTouch = verseTouch(def);
      hits.push(p);
      this.board[layer].flash = 0.4;
      this.emitBoardFx({ type: "silence", layer, slot, side: "enemy" });
    }
    if (!hits.length) {
      this.log(`${def.reference}: no Confusion to cast down.`, at);
      return;
    }
    this.stats.liesSilenced += hits.length;
    this.log(
      `${def.reference}: cast down ${hits.length} Confusion` +
        (hits.length === 1 ? "" : "s") +
        ".",
      at,
    );
    this.emitAudio({
      type: "lie.silence",
      lieId: hits[0]!.instanceId,
      lieType: "confusion",
      layer: "mind",
    });
    // Brief Still on leftover residuals so they don't remorph immediately
    for (const { presence: p } of allLies(this.board)) {
      if (p.side !== "enemy" || p.level !== 1) continue;
      if (p.lieType === "confusion" || isDogmaPresence(p) || isDoubtPresence(p)) {
        continue;
      }
      p.slowTimer = Math.max(p.slowTimer, 3);
      p.slowFactor = Math.min(p.slowFactor, 0.5);
      p.confuseTimer = 0;
    }
    this.cleanupDead();
  }

  private tryCastDown(def: VerseDef, at: LogOrigin): boolean {
    for (const { presence: p, layer } of allLies(this.board)) {
      if (!isDogmaPresence(p) || !p.dogma) continue;
      if (!dogmaIsCore(p.dogma)) continue;
      this.resolveDogmaHit(p, layer, def, { kind: "castDown", logAt: at });
      return true;
    }
    return false;
  }

  private applyLayAside(
    playedInstanceId: string,
    chosenId?: string,
  ): void {
    const others = this.hand.filter((c) => c.instanceId !== playedInstanceId);
    if (!others.length) {
      this.log("Nothing in hand to lay aside.", "system");
      return;
    }
    let pick =
      (chosenId
        ? others.find((c) => c.instanceId === chosenId)
        : undefined) ?? this.autoLayAside(others);
    const idx = this.hand.findIndex((c) => c.instanceId === pick.instanceId);
    if (idx < 0) return;
    this.hand.splice(idx, 1);
    this.discardPile.push(pick.def.id);
    this.drawCard({ animate: true });
    this.log(
      `Laid aside ${pick.def.reference} — a Word for another hour.`,
      "truth",
    );
  }

  private autoLayAside(others: CardInstance[]): CardInstance {
    const brick = others.find((c) => {
      const d = c.def;
      if (d.selector === "restorePrayer") {
        let damaged = false;
        forEachPresence(this.board, (pr) => {
          if (pr.side === "player" && pr.level < pr.maxLevel) damaged = true;
        });
        return !damaged;
      }
      if (d.selector === "convertLie") {
        return !allLies(this.board).some((l) => isLieRedeemable(l.presence));
      }
      if (d.selector === "gatherPrayers") return true;
      if (d.chipOnlyTypes?.length === 1 && d.chipOnlyTypes[0] === "fear") {
        return !allLies(this.board).some((l) => l.presence.lieType === "fear");
      }
      if (d.selector === "empower") return true;
      return false;
    });
    return brick ?? others[others.length - 1]!;
  }

  /** Sync presence level / claim / meta from onion state after a hit or decay. */
  private syncDoubtPresence(p: Presence): void {
    if (!p.doubt) return;
    const def = lieById(p.defId);
    if (!def) return;
    const remaining = doubtRemaining(p.doubt);
    applyLieStage(p, def, Math.max(1, remaining));
  }

  /**
   * Resolve Answer / chip against a Doubt onion (convert, revert, or complete).
   * Returns true if the hit was handled as Doubt (caller must not classic step).
   */
  private resolveDoubtHit(
    p: Presence,
    layer: LayerId,
    def: VerseDef,
    opts: {
      kind: DoubtHitKind;
      correctTarget?: boolean;
      empowerBonus?: number;
      logAt?: LogOrigin;
    },
  ): boolean {
    if (!isDoubtPresence(p) || !p.doubt) return false;

    const meets = convincesDoubt(def.category, p.lieType, def.strongVs);
    const strength = doubtHitStrength(def, {
      kind: opts.kind,
      meets,
      correctTarget: opts.correctTarget,
      empowerBonus: opts.empowerBonus,
    });
    const { state, result } = applyDoubtHit(p.doubt, {
      meets,
      strength,
      kind: opts.kind,
    });
    p.doubt = state;
    if (opts.kind === "direct" || result.ringsChanged > 0 || result.completed) {
      p.lastTouch = verseTouch(def);
    }

    const at = opts.logAt ?? this.originOf(layer, p.homeSlot);
    const prog = doubtFaithProgress(state);
    const slot = p.homeSlot;

    if (result.completed) {
      // Doubt always Redeems — never mere silence — whether finished by
      // Faith Answer or a dedicated Redeem card (convertLie path).
      let liveSlot = slot;
      for (let i = 0; i < this.board[layer].slots.length; i++) {
        if (this.board[layer].slots[i]?.instanceId === p.instanceId) {
          liveSlot = i;
          break;
        }
      }
      const was = p.name;
      const lieLv = Math.max(1, p.level);
      const lieType = p.lieType;
      const { level: lv, elite } = redeemPrayerLevel(def, lieLv);
      const prayerName = def.convertPrayerName ?? "Settled Faith";
      const prayer = makeRedeemPrayer(def, liveSlot, prayerName, lv, elite);
      this.board[layer].slots[liveSlot] = prayer;
      this.stats.liesSilenced++;
      this.stats.prayersPlaced++;
      this.stats.redeems++;
      const tiltPush = -10 - lv * 2 - (elite ? 4 : 0);
      this.board[layer].tilt = clampTilt(this.board[layer].tilt + tiltPush);
      this.board[layer].flash = 0.55;
      this.log(
        `${was} settled in Faith · ${def.reference} · ` +
          `${prayerName} (${levelRoman(lv)}${elite ? " fortified" : ""}) abides in ${LAYER_LABELS[layer]}`,
        this.originOf(layer, liveSlot),
      );
      this.emitAudio({
        type: "redeem",
        verseId: def.id,
        lieType,
        level: lieLv,
      });
      this.emitBoardFx({
        type: "redeem",
        layer,
        slot: liveSlot,
      });
      return true;
    }

    if (result.action === "noop") {
      this.emitBoardFx({
        type: "strike",
        layer,
        slot,
        quality: "miss",
        steps: 0,
      });
      this.log(`${def.reference}: ${p.name} — ${result.summary}.`, at);
      return true;
    }

    this.syncDoubtPresence(p);

    if (result.action === "revert" || result.action === "tangle") {
      this.emitBoardFx({
        type: "degrade",
        layer,
        slot,
        side: "enemy",
        fromLevel: Math.max(1, result.outerCountBefore + 1),
        toLevel: Math.max(1, result.outerCountAfter + 1),
      });
      this.emitAudio({
        type: "answer.resolve",
        verseId: def.id,
        steps: 0,
        meet: false,
        correctTarget: false,
        lieType: "doubt",
      });
      if (result.action === "tangle") {
        this.board[layer].flash = 0.45;
      }
    } else {
      this.emitBoardFx({
        type: "strike",
        layer,
        slot,
        quality: "meet",
        steps: result.ringsChanged,
      });
      this.emitAudio({
        type: "answer.resolve",
        verseId: def.id,
        steps: result.ringsChanged,
        meet: true,
        correctTarget: !!opts.correctTarget,
        lieType: "doubt",
      });
      this.stats.lieLevelDrops++;
    }

    const timerBit =
      result.action === "tangle" && state.decayEnabled
        ? " · re-doubt timer started"
        : "";
    this.log(
      `${def.reference}: ${p.name} — ${result.summary} · ${prog.faith}/${prog.total} Faith` +
        (doubtCoreExposed(state)
          ? " · core open (Faith Answer or Redeem → Prayer)"
          : "") +
        timerBit,
      at,
    );
    return true;
  }

  /**
   * Combat log + discourse feed.
   * @param kindOrOrigin LogKind string, or board origin when kind is inferred
   * @param origin Board anchor when kind is passed as 2nd arg
   */
  private log(
    msg: string,
    kindOrOrigin?: LogKind | LogOrigin,
    origin?: LogOrigin,
  ): void {
    let kind: LogKind | undefined;
    let at: LogOrigin | undefined;
    if (typeof kindOrOrigin === "string") {
      kind = kindOrOrigin;
      at = origin;
    } else if (kindOrOrigin && typeof kindOrOrigin === "object") {
      at = kindOrOrigin;
    }
    const entry: CombatLogEntry = {
      seq: ++this.logSeq,
      t: this.elapsed,
      wall: new Date().toISOString(),
      kind: kind ?? classifyLogMessage(msg),
      message: msg,
      origin: at,
    };
    this.combatLog.push(entry);
    this.lastMessage = msg;
    for (const fn of this.logListeners) fn(msg, entry);
  }

  private originOf(layer: LayerId, slot?: number): LogOrigin {
    return slot === undefined ? { layer } : { layer, slot };
  }

  /** Snapshot of stats + log for the end screen / copy-debug. */
  getRunReport(): { stats: RunStats; log: CombatLogEntry[] } {
    this.finalizeStats();
    return {
      stats: { ...this.stats, versePlayOrder: [...this.stats.versePlayOrder] },
      log: [...this.combatLog],
    };
  }

  private finalizeStats(): void {
    this.stats.status = this.status;
    this.stats.elapsed = this.elapsed;
    this.stats.integrityEnd = this.resources.integrity;
    this.stats.difficulty = this.difficulty;
  }

  private freshStats(difficulty: Difficulty): RunStats {
    return emptyRunStats({
      scenarioId: this.scenario.id,
      scenarioName: this.scenario.name,
      difficulty,
      duration: this.scenario.duration,
      deckPoolSize: 0,
    });
  }

  reset(difficulty: Difficulty): void {
    this.difficulty = difficulty;
    this.status = "playing";
    this.elapsed = 0;
    this.userPaused = false;
    this.paused = false;
    this.pendingRevealId = null;
    this.firedSpawns = new Set();
    this.escalation = createEscalationRuntime();
    this.waveLabel = "Prepare";
    this.combatLog = [];
    this.logSeq = 0;
    this.endLogged = false;
    this.dogmaNotice = null;
    this.dogmaNoticed = false;
    this.crowdLogged = new Set();
    this.stats = this.freshStats(difficulty);
    this.lastMessage = "";
    this.lastCheckpointKey = "";
    this.findingsThisRun = [];
    presenceSeq = 1;
    cardSeq = 1;
    this.pendingSteps = [];

    const s = this.scenario;
    this.resources = createResources({
      integrity: s.maxIntegrity,
    });
    this.stats.minIntegrity = s.maxIntegrity;
    this.board = createEmptyBoard();
    if (this.forcedDeckPool?.length) {
      this.deckPool = [...this.forcedDeckPool];
    } else {
      this.deckPool = resolvePlayableDeck(STARTER_DECK_IDS);
    }
    this.proclaimLock = 0;
    this.proclaimLockMax = 0;
    this.proclaimRef = "";
    this.proclaimText = "";
    this.proclaimWordIndex = 0;
    this.proclaimWordCount = 0;
    this.proclaimVerseId = "";
    this.empowerCharges = 0;
    this.empowerBonusSteps = 1;
    this.speakHasteCharges = 0;
    this.speakHasteFactor = 1;
    this.joyField = null;
    this.stats.deckPoolSize = this.deckPool.length;
    this.discardPile = [];
    // One copy of each entry in deckPool (multiplicity allowed). No artificial double.
    this.deck = shuffle([...this.deckPool]);
    this.hand = [];
    for (let i = 0; i < s.handSize; i++) this.drawCard();

    const timeNote = this.isEndless()
      ? "endless · survive"
      : `${s.duration}s`;
    this.log(
      `Mission start · ${s.name} (${s.id}) · ${difficulty} · ${timeNote} · deck ${this.deckPool.length} (draw ${this.deck.length} · hand ${this.hand.length})`,
      "meta",
    );
    this.log(
      this.isEndless()
        ? "Endless free play — timer counts up; your time is the score. Hold Will."
        : "Lies speak. Proclaim the Word — speech is the resource. Hold Will.",
      "system",
    );
    this.emitAudio({ type: "mission.start" });
  }

  snapshot(): GameSnapshot {
    return {
      status: this.status,
      resources: this.resources,
      hand: this.hand,
      board: this.board,
      elapsed: this.elapsed,
      duration: this.scenario.duration,
      endless: this.isEndless(),
      waveLabel: this.waveLabel,
      lastMessage: this.lastMessage,
      paused: this.paused,
      difficulty: this.difficulty,
      pendingRevealId: this.pendingRevealId,
      combatLogLength: this.combatLog.length,
      proclaimLock: this.proclaimLock,
      proclaimLockMax: this.proclaimLockMax,
      proclaimRef: this.proclaimRef,
      proclaimText: this.proclaimText,
      proclaimWordIndex: this.proclaimWordIndex,
      proclaimWordCount: this.proclaimWordCount,
      proclaimVerseId: this.proclaimVerseId,
      drawPileSize: this.deck.length,
      discardPileSize: this.discardPile.length,
      deckTotalSize:
        this.deck.length + this.hand.length + this.discardPile.length,
      settledHunt: this.settledHuntSnapshot(),
    };
  }

  private settledHuntSnapshot(): SettledHunt | null {
    for (const layer of LAYER_ORDER) {
      for (let slot = 0; slot < this.board[layer].slots.length; slot++) {
        const p = this.board[layer].slots[slot];
        if (!p || !isDogmaPresence(p) || !p.dogma || p.level <= 0) continue;
        return {
          name: p.name,
          claim: p.claim,
          layer,
          slot,
          demand: dogmaDemand(p.dogma),
          named: p.dogma.named || dogmaIsCore(p.dogma),
          remaining: dogmaRemaining(p.dogma),
          total: p.dogma.sequence.length,
          onWill: layer === "will",
        };
      }
    }
    return null;
  }

  /** True while a verse is being spoken. */
  isProclaiming(): boolean {
    return this.proclaimLock > 1e-6;
  }

  /** True if this hand card would spend an Empower charge when played. */
  isEmpowerHighlighted(def: VerseDef): boolean {
    return this.empowerCharges > 0 && isEmpowerableInstant(def);
  }

  /**
   * Spend one Empower charge if available. Returns bonus steps granted (0 if none).
   * Soft-cap: caller should min with Answer cap (3).
   */
  private takeEmpowerBonus(def: VerseDef): number {
    if (this.empowerCharges <= 0) return 0;
    if (!isEmpowerableInstant(def)) return 0;
    const bonus = Math.max(1, this.empowerBonusSteps);
    this.empowerCharges -= 1;
    this.log(
      `Empower spent` +
        (this.empowerCharges > 0
          ? ` · ${this.empowerCharges} charge(s) left`
          : " · charges spent") +
        ` · +${bonus} step`,
      "truth",
    );
    return bonus;
  }

  private syncPause(): void {
    const revealHold =
      this.difficulty === "easy" && this.pendingRevealId !== null;
    this.paused = this.userPaused || revealHold;
  }

  setUserPaused(paused: boolean): void {
    if (this.status !== "playing") return;
    const was = this.userPaused;
    this.userPaused = paused;
    this.syncPause();
    if (this.userPaused) {
      this.log("Paused — Space or Pause to resume.");
    } else if (!this.paused) {
      this.log("Resumed.");
    }
    if (was !== this.userPaused) {
      this.emitAudio({ type: "pause", paused: this.userPaused });
    }
  }

  toggleUserPause(): void {
    if (this.status !== "playing") return;
    this.setUserPaused(!this.userPaused);
  }

  openReveal(instanceId: string): CardInstance | null {
    if (this.status !== "playing") return null;
    const card = this.hand.find((c) => c.instanceId === instanceId);
    if (!card) return null;
    const wasOpen = this.pendingRevealId !== null;
    this.pendingRevealId = instanceId;
    this.syncPause();
    this.stats.reveals++;
    if (!wasOpen) {
      this.log(`Studied ${card.def.reference}`, "meta");
      this.emitAudio({ type: "reveal.open" });
    }
    return card;
  }

  closeReveal(): void {
    const wasOpen = this.pendingRevealId !== null;
    this.pendingRevealId = null;
    this.syncPause();
    if (wasOpen) this.emitAudio({ type: "reveal.close" });
  }

  discard(instanceId: string): boolean {
    const idx = this.hand.findIndex((c) => c.instanceId === instanceId);
    if (idx < 0) return false;
    const [card] = this.hand.splice(idx, 1);
    if (card) this.discardPile.push(card.def.id);
    if (this.pendingRevealId === instanceId) this.closeReveal();
    return true;
  }

  paidDraw(_cost = 2): boolean {
    if (this.status !== "playing" || this.paused) return false;
    if (this.isProclaiming()) {
      this.log("Still proclaiming — wait for the Word to finish.");
      return false;
    }
    if (this.hand.length >= this.scenario.handSize) return false;
    // P3: free recall from draw pile (not the whole library)
    if (!this.drawCard()) return false;
    this.stats.paidDraws++;
    this.log(
      `Recalled a verse · draw ${this.deck.length} · used ${this.discardPile.length}`,
      "system",
    );
    return true;
  }

  /** Shuffle discard into draw when the draw pile is empty. */
  private ensureDrawPile(): boolean {
    if (this.deck.length > 0) return true;
    if (this.discardPile.length === 0) return false;
    this.deck = shuffle(this.discardPile);
    this.discardPile = [];
    this.log(
      `Shuffled used pile into draw (${this.deck.length} verses)`,
      "system",
    );
    return true;
  }

  private drawCard(opts?: { animate?: boolean; maxHand?: number }): boolean {
    const cap = opts?.maxHand ?? this.scenario.handSize;
    if (this.hand.length >= cap) return false;
    if (!this.ensureDrawPile()) return false;
    const id = this.deck.pop();
    if (!id) return false;
    const def = verseById(id);
    if (!def) return false;
    this.hand.push({
      instanceId: `c-${cardSeq++}`,
      def,
      justDrawn: opts?.animate ?? false,
    });
    return true;
  }

  /**
   * Headless / balance sims: fixed loadout, reshuffled hand.
   * Call after reset().
   */
  forceReseedDeck(pool: string[]): void {
    this.deckPool = pool.length ? [...pool] : [...STARTER_DECK_IDS];
    this.discardPile = [];
    this.deck = shuffle([...this.deckPool]);
    this.hand = [];
    for (let i = 0; i < this.scenario.handSize; i++) this.drawCard();
  }

  /**
   * Proclaim a hand card. Speech lock is the resource (no Spirit).
   * Effect resolves immediately; lock prevents the next proclaim until speak ends.
   */
  activate(instanceId: string, intent?: DragIntent | null): boolean {
    if (this.status !== "playing" || this.paused) {
      if (this.paused && this.status === "playing") {
        this.log("Paused — resume to proclaim Scripture.");
      }
      return false;
    }
    if (this.isProclaiming()) {
      this.log("Still proclaiming — wait for the Word to finish.");
      return false;
    }
    const idx = this.hand.findIndex((c) => c.instanceId === instanceId);
    if (idx < 0) return false;
    const card = this.hand[idx]!;
    const def = card.def;

    if (def.kind === "placeable") {
      const placed = this.resolvePlaceable(def, intent ?? null, /*commit*/ false);
      if (!placed.ok) {
        this.log(placed.reason || `${def.reference} found no valid target.`);
        this.emitAudio({ type: "ui.illegal" });
        return false;
      }
      // Combat resolve emits happen inside resolve* (commit); then proclaim.start
      this.resolvePlaceable(def, intent ?? null, /*commit*/ true);
    } else {
      const preview = this.resolveInstant(def, intent ?? null, /*commit*/ false);
      if (!preview.ok) {
        this.log(preview.reason || `${def.reference} found no valid target.`);
        this.emitAudio({ type: "ui.illegal" });
        return false;
      }
      this.resolveInstant(def, intent ?? null, /*commit*/ true);
    }

    this.beginProclaim(def);

    if (def.kind === "instant" && def.layAside) {
      this.applyLayAside(instanceId, intent?.layAsideInstanceId);
    }

    // Played card → used pile; draw a replacement from the draw pile
    this.hand.splice(idx, 1);
    this.discardPile.push(def.id);
    if (this.pendingRevealId === instanceId) this.closeReveal();

    this.drawCard({ animate: true });

    // Extra recalls (e.g. 1 Thess 5:17) — may briefly overfill the hand
    const extra = def.kind === "instant" ? def.handDraw ?? 0 : 0;
    if (extra > 0) {
      let drawn = 0;
      const maxHand = this.scenario.handSize + extra;
      for (let i = 0; i < extra; i++) {
        if (this.drawCard({ animate: true, maxHand })) drawn++;
      }
      if (drawn > 0) {
        this.stats.paidDraws += drawn;
        this.log(
          `${def.reference}: recalled ${drawn} more verse${drawn === 1 ? "" : "s"}`,
          "system",
        );
      }
    }

    this.stats.versesPlayed++;
    this.stats.versePlayOrder.push(def.id);
    if (def.kind === "placeable") {
      this.stats.placeablesPlayed++;
      this.stats.prayersPlaced++;
    } else {
      this.stats.instantsPlayed++;
      if (def.selector === "convertLie") this.stats.redeems++;
    }
    return true;
  }

  private beginProclaim(def: VerseDef): void {
    const text = proclaimLine(def);
    let secs = Math.max(0.8, speakDuration(def));
    // Haste is applied during resolveInstant before this; do not spend it on the grant.
    const isHasteGrant = !!def.speakHaste;
    if (!isHasteGrant && this.speakHasteCharges > 0) {
      secs = Math.max(0.8, secs * this.speakHasteFactor);
      this.speakHasteCharges -= 1;
      this.log(
        `Prayer haste · speak ${secs.toFixed(1)}s` +
          (this.speakHasteCharges > 0
            ? ` · ${this.speakHasteCharges} left`
            : ""),
        "truth",
      );
    }
    this.proclaimVerseId = def.id;
    this.proclaimText = text;
    this.proclaimRef = def.reference;
    this.proclaimLockMax = secs;
    this.proclaimLock = secs;
    const words = text.split(/\s+/).filter(Boolean);
    this.proclaimWordCount = words.length;
    this.proclaimWordIndex = 0;
    this.log(`Proclaiming ${def.reference} (${secs.toFixed(1)}s)`, "truth");
    // After combat resolve event(s) from activate path
    this.emitAudio({
      type: "proclaim.start",
      verseId: def.id,
      category: def.category,
      speakSeconds: secs,
      ref: def.reference,
    });
  }

  private resolvePlaceable(
    def: VerseDef,
    intent: DragIntent | null,
    commit: boolean,
  ): { ok: boolean; reason: string } {
    if (!def.placeable) {
      return { ok: false, reason: `${def.reference} is not placeable.` };
    }

    let layer: LayerId | null = null;
    let slot: number | null = null;
    const mask = def.respectMask;

    const tryLayer = (L: LayerId, preferredSlot?: number): boolean => {
      if (
        preferredSlot !== undefined &&
        canPlaceInSlot(this.board, L, preferredSlot, "player")
      ) {
        layer = L;
        slot = preferredSlot;
        return true;
      }
      const order =
        preferredSlot !== undefined
          ? [preferredSlot, 1, 0, 2]
          : [1, 0, 2];
      for (const s of order) {
        if (s < 0 || s > 2) continue;
        if (canPlaceInSlot(this.board, L, s, "player") && !this.board[L].slots[s]) {
          layer = L;
          slot = s;
          return true;
        }
      }
      for (const s of order) {
        if (s < 0 || s > 2) continue;
        if (canPlaceInSlot(this.board, L, s, "player")) {
          layer = L;
          slot = s;
          return true;
        }
      }
      return false;
    };

    if (intent) {
      if (mask === "exactSlot" || mask === "adjacentExpand") {
        // Honor the slot the player dropped on when it is legal.
        if (canPlaceInSlot(this.board, intent.layer, intent.slot, "player")) {
          layer = intent.layer;
          slot = intent.slot;
        } else if (mask === "adjacentExpand") {
          // Path prayer: soft-redirect within the aimed layer before global auto.
          tryLayer(intent.layer, intent.slot);
        }
      } else if (mask === "layerOnly" || mask === "layerPrefer") {
        // Prefer the aimed slot on that layer. (Bug: used to call tryLayer(layer)
        // with no preferredSlot → always center-first [1,0,2], skipping empty
        // side slots the player actually selected.)
        tryLayer(intent.layer, intent.slot);
      }
    }

    if (layer === null) {
      // exactSlot with a failed aim: do not auto-warp elsewhere.
      // layerOnly already exhausted the aimed layer above.
      const allowAuto =
        !intent || (mask !== "exactSlot" && mask !== "layerOnly");
      if (allowAuto) {
        const order: LayerId[] = [];
        if (def.preferLayer) order.push(def.preferLayer);
        for (const L of ["will", "heart", "mind"] as LayerId[]) {
          if (!order.includes(L)) order.push(L);
        }
        for (const L of order) {
          // Already tried this layer under the intent branch above.
          if (intent && L === intent.layer) continue;
          if (tryLayer(L, intent?.slot)) break;
        }
      }
    }

    if (layer === null || slot === null) {
      return {
        ok: false,
        reason: `${def.reference}: no legal slot (need foothold + empty or friendly slot).`,
      };
    }

    if (!commit) return { ok: true, reason: "" };

    const replaced = this.board[layer].slots[slot];
    const presence = makePlayerPresence(def, slot);
    this.board[layer].slots[slot] = presence;
    const rep = replaced ? ` (replaced ${replaced.name})` : "";
    this.log(
      `${def.reference} abides in the ${LAYER_LABELS[layer]}${rep}`,
      this.originOf(layer, slot),
    );
    this.emitAudio({
      type: "placeable.abide",
      verseId: def.id,
      layer,
    });
    return { ok: true, reason: "" };
  }

  private resolveInstant(
    def: VerseDef,
    intent: DragIntent | null,
    commit: boolean,
  ): { ok: boolean; reason: string } {
    const sel = def.selector ?? "strongestLie";

    if (sel === "empower") {
      if (!commit) return { ok: true, reason: "" };
      const charges = Math.max(1, def.empowerCharges ?? 1);
      const bonus = Math.max(1, def.empowerBonusSteps ?? 1);
      // Stack charges if double-Empower; refresh bonus to latest card's value
      this.empowerCharges += charges;
      this.empowerBonusSteps = bonus;
      const at = this.originOf("will", 1);
      this.log(
        `${def.reference}: Empower +${bonus} on next ${charges} Answer/Chip` +
          (this.empowerCharges > charges
            ? ` · ${this.empowerCharges} total charges`
            : "") +
          ".",
        at,
      );
      this.emitAudio({ type: "instant.self", verseId: def.id });
      return { ok: true, reason: "" };
    }

    if (sel === "gatherPrayers") {
      const host = this.pickGatherHost(intent);
      if (!host) {
        const anyPrayer = this.allPlayerPrayers().length > 0;
        const anyDamaged = this.allPlayerPrayers().some(
          (x) => x.presence.level < x.presence.maxLevel,
        );
        return {
          ok: false,
          reason: !anyPrayer
            ? `${def.reference}: no Prayer on the board to gather into.`
            : !anyDamaged
              ? `${def.reference}: every keep is already full strength — card stays in hand.`
              : `${def.reference}: need another residual (I) Prayer to lift this one — card stays in hand.`,
        };
      }
      const plan = planGather(host, this.allPlayerPrayers());
      if (!plan.ok) {
        return {
          ok: false,
          reason: `${def.reference}: ${plan.reason ?? "cannot gather"} — card stays in hand.`,
        };
      }
      if (!commit) return { ok: true, reason: "" };

      const fromLevel = host.presence.level;
      const donorNames: string[] = [];
      for (const d of plan.donors) {
        donorNames.push(d.presence.name);
        // Remove donor — gathered into host (not a broken keep)
        this.board[d.layer].slots[d.slot] = null;
      }
      this.setPrayerLevel(host.presence, plan.toLevel);
      this.board[host.layer].tilt = clampTilt(
        this.board[host.layer].tilt - 6 - plan.donors.length * 2,
      );
      this.board[host.layer].flash = 0.5;
      const at = this.originOf(host.layer, host.slot);
      this.log(
        `${def.reference}: Gather · ${host.presence.name} ${levelRoman(fromLevel)}→${levelRoman(plan.toLevel)} in ${LAYER_LABELS[host.layer]}` +
          (donorNames.length
            ? ` · lifted by ${donorNames.join(", ")}`
            : "") +
          ".",
        at,
      );
      this.emitAudio({ type: "instant.self", verseId: def.id });
      this.emitBoardFx({
        type: "strike",
        layer: host.layer,
        slot: host.slot,
        quality: "meet",
        steps: plan.donors.length,
      });
      this.cleanupDead();
      return { ok: true, reason: "" };
    }

    if (sel === "restorePrayer") {
      const target = this.pickDamagedPrayer(intent);
      if (!target) {
        const anyPrayer = this.allPlayerPrayers().length > 0;
        return {
          ok: false,
          reason: anyPrayer
            ? `${def.reference}: no damaged Prayer to restore — keep stays in hand.`
            : `${def.reference}: no Prayer on the board to restore.`,
        };
      }
      if (!commit) return { ok: true, reason: "" };

      const p = target.presence;
      const from = p.level;
      this.restorePrayerToMax(p);

      // Renew fortify: half wear for a short time (“run and not be weary”)
      const fort = def.fortifyAfterRestore;
      if (fort && fort.duration > 0) {
        const fac = Math.min(1, Math.max(0.15, fort.wearFactor ?? 0.5));
        p.fortifyTimer = Math.max(p.fortifyTimer, fort.duration);
        p.fortifyWearFactor = Math.min(p.fortifyWearFactor || 1, fac);
      }

      // Lightly renew neighbors on the same layer
      let adjacentMended = 0;
      const mend = def.mendAdjacent;
      if (mend) {
        const slots = this.board[target.layer].slots;
        for (let i = 0; i < slots.length; i++) {
          if (i === target.slot) continue;
          const n = slots[i];
          if (!n || n.side !== "player" || n.level <= 0) continue;
          let changed = false;
          if (mend.clearWear !== false && n.wear > 0) {
            n.wear = 0;
            changed = true;
          }
          const levels = Math.max(0, Math.floor(mend.levels ?? 0));
          if (levels > 0 && n.level < n.maxLevel) {
            const to = Math.min(n.maxLevel, n.level + levels);
            if (to > n.level) {
              n.level = to;
              n.wear = 0;
              n.meta = `${n.category ?? "prayer"} · ${levelRoman(to)}`;
              // Restore verbs if this is a full recover to max
              if (to >= n.maxLevel) this.restorePrayerToMax(n);
              changed = true;
            }
          }
          if (changed) {
            n.anim = "spawn";
            n.animTime = 0.28;
            adjacentMended++;
          }
        }
      }

      // Soft Truth tilt on that layer
      this.board[target.layer].tilt = clampTilt(
        this.board[target.layer].tilt - 8,
      );
      this.board[target.layer].flash = 0.45;
      const at = this.originOf(target.layer, target.slot);
      const fortBit =
        fort && fort.duration > 0
          ? ` · Renewed ${fort.duration.toFixed(0)}s (half wear)`
          : "";
      const adjBit =
        adjacentMended > 0
          ? ` · +${adjacentMended} nearby Prayer${adjacentMended === 1 ? "" : "s"} eased`
          : "";
      this.log(
        `${def.reference}: Restore Keep · ${p.name} ${levelRoman(from)}→${levelRoman(p.level)} in ${LAYER_LABELS[target.layer]}${fortBit}${adjBit}`,
        at,
      );
      this.emitAudio({ type: "instant.self", verseId: def.id });
      return { ok: true, reason: "" };
    }

    if (sel === "self") {
      const hasEffect =
        !!def.restoreIntegrity ||
        !!(def.handDraw && def.handDraw > 0) ||
        !!def.speakHaste ||
        !!def.joyField ||
        !!def.wipeConfusion ||
        !!def.castDown ||
        !!def.layAside;
      if (!hasEffect) {
        return { ok: false, reason: `${def.reference}: nothing to resolve.` };
      }
      if (!commit) return { ok: true, reason: "" };

      // Self effects: float near Will (Secret Place feed)
      const selfAt = this.originOf("will", 1);
      this.log(`${def.reference} (self)`, selfAt);
      if (def.wipeConfusion) {
        this.wipeAllConfusion(def, selfAt);
      }
      if (def.castDown) {
        this.tryCastDown(def, selfAt);
      }
      if (def.joyField) {
        this.beginJoyField(def);
      }
      if (def.restoreIntegrity) {
        let amt = def.restoreIntegrity;
        // Plain heal on Joy signature — no huge Despair bonus (field is the main pay-off)
        if (
          !def.joyField &&
          allLies(this.board).some((x) => x.presence.lieType === "despair")
        ) {
          amt += 6;
        }
        this.resources.integrity = Math.min(
          this.resources.maxIntegrity,
          this.resources.integrity + amt,
        );
        this.log(`${def.reference}: restored ${amt} Integrity`, selfAt);
      }
      if (def.speakHaste) {
        const charges = Math.max(1, def.speakHaste.charges ?? 1);
        const factor = Math.min(1, Math.max(0.4, def.speakHaste.factor));
        this.speakHasteCharges += charges;
        this.speakHasteFactor = factor;
        const pct = Math.round((1 - factor) * 100);
        this.log(
          `${def.reference}: next ${charges} proclaim${charges === 1 ? "" : "s"} ${pct}% faster`,
          selfAt,
        );
      }
      if (def.handDraw && def.handDraw > 0) {
        // Actual draws apply after the card leaves the hand (activate).
        this.log(
          `${def.reference}: will recall ${def.handDraw} extra verse${def.handDraw === 1 ? "" : "s"}`,
          selfAt,
        );
      }
      this.emitAudio({ type: "instant.self", verseId: def.id });
      return { ok: true, reason: "" };
    }

    if (sel === "layerSlam" || sel === "slowLayer" || sel === "layerChip") {
      let layer: LayerId | null = null;
      if (def.lockLayer) {
        layer = def.lockLayer;
      } else if (intent) {
        layer = intent.layer;
      }
      if (!layer && def.preferLayer) layer = def.preferLayer;
      if (!layer) {
        layer = LAYER_ORDER.reduce((best, id) =>
          this.board[id].tilt > this.board[best].tilt ? id : best,
        );
      }
      if (!commit) return { ok: true, reason: "" };

      const layerAt = this.originOf(layer, 1);
      this.log(`${def.reference} → ${LAYER_LABELS[layer]}`, layerAt);

      if (sel === "slowLayer") {
        const dur = def.slowDuration ?? 4;
        const fac = def.slowFactor ?? 0.4;
        let n = 0;
        for (const p of this.board[layer].slots) {
          if (p?.side === "enemy") {
            p.slowTimer = Math.max(p.slowTimer, dur);
            p.slowFactor = Math.min(p.slowFactor, fac);
            p.advanceProgress *= 0.55;
            p.advanceTimer *= 0.55;
            n++;
          }
        }
        if (def.tiltSlam) {
          this.board[layer].tilt = clampTilt(
            this.board[layer].tilt + def.tiltSlam,
          );
        }
        this.board[layer].flash = 0.45;
        this.log(
          `${def.reference}: Still on ${LAYER_LABELS[layer]}` +
            (n ? ` · ${n} lie(s) slowed ${dur.toFixed(0)}s` : " · no lies present") +
            ".",
          layerAt,
        );
        this.emitAudio({
          type: "instant.layer",
          verseId: def.id,
          selector: sel,
          layer,
        });
        return { ok: true, reason: "" };
      }

      // layerChip / legacy layerSlam
      const tiltAmt = def.tiltSlam ?? 0;
      if (tiltAmt) {
        this.board[layer].tilt = clampTilt(this.board[layer].tilt + tiltAmt);
      }
      this.board[layer].flash = 0.45;

      let chipSteps = Math.max(1, def.answerLevels ?? 1);
      const empowerBonus = this.takeEmpowerBonus(def);
      if (empowerBonus > 0) {
        chipSteps = Math.min(3, chipSteps + empowerBonus);
      }
      const only = def.chipOnlyTypes;
      const enemies = this.board[layer].slots
        .map((p, i) => ({ p, i }))
        .filter((o) => {
          if (!o.p || o.p.side !== "enemy") return false;
          if (only?.length && o.p.lieType && !only.includes(o.p.lieType)) {
            // Confusion is weak to every tool — still takes specialized chips
            if (o.p.lieType !== "confusion") return false;
          }
          if (sel === "layerSlam" && def.strongVs?.length) {
            return (
              !!o.p.lieType && def.strongVs.includes(o.p.lieType)
            );
          }
          return true;
        }) as { p: Presence; i: number }[];

      let hits = 0;
      for (const { p, i } of enemies) {
        p.homeSlot = i;
        if (isDogmaPresence(p)) {
          this.resolveDogmaHit(p, layer, def, {
            kind: "chip",
            logAt: layerAt,
          });
          hits++;
          continue;
        }
        if (isDoubtPresence(p)) {
          this.resolveDoubtHit(p, layer, def, {
            kind: "chip",
            empowerBonus,
            logAt: layerAt,
          });
          hits++;
          continue;
        }
        const meet = this.meetFields(def, p.lieType);
        this.emitBoardFx({
          type: "strike",
          layer,
          slot: i,
          quality: strikeQuality(chipSteps, meet, false),
          steps: chipSteps,
        });
        this.stepPresence(p, layer, chipSteps, {
          touch: verseTouch(def),
          forceSteps: chipSteps,
          skipProclaim: false,
          fromVerse: def,
        });
        hits++;
      }
      this.flushSteps();

      // Optional Still rider on the chipped layer (peace that calms the row)
      let stillN = 0;
      if (def.slowDuration && def.slowDuration > 0) {
        const dur = def.slowDuration;
        const fac = def.slowFactor ?? 0.4;
        for (const p of this.board[layer].slots) {
          if (p?.side === "enemy" && p.level > 0) {
            p.slowTimer = Math.max(p.slowTimer, dur);
            p.slowFactor = Math.min(p.slowFactor, fac);
            p.advanceProgress *= 0.55;
            p.advanceTimer *= 0.55;
            stillN++;
          }
        }
      }

      const scope =
        only?.length === 1
          ? `${only[0]} `
          : only?.length
            ? "matching "
            : "";
      this.log(
        `${def.reference}: ${sel === "layerChip" ? "chip" : "peace over"} ${LAYER_LABELS[layer]}` +
          (hits
            ? ` · −${chipSteps} on ${hits} ${scope}lie(s)`
            : " · no matching lies") +
          (stillN
            ? ` · Still ${def.slowDuration!.toFixed(0)}s on ${stillN}`
            : "") +
          (empowerBonus ? " · Empowered" : "") +
          ".",
        layerAt,
      );
      this.emitAudio({
        type: "instant.layer",
        verseId: def.id,
        selector: sel,
        layer,
      });
      return { ok: true, reason: "" };
    }

    if (sel === "convertLie") {
      // Exact drag onto a full-strength lie: hard fail (no silent retarget)
      if (intent) {
        const exact = allLies(this.board).find(
          (l) => l.layer === intent.layer && l.slot === intent.slot,
        );
        if (exact && !isLieRedeemable(exact.presence)) {
          const doubtHint =
            isDoubtPresence(exact.presence) && exact.presence.doubt
              ? " — peel outer questions to Faith first (core still closed)."
              : " — answer it once before Redeem.";
          return {
            ok: false,
            reason: `${def.reference}: ${exact.presence.name} is not ready${doubtHint}`,
          };
        }
      }

      const target = this.pickLieTarget(def, intent, isLieRedeemable);
      if (!target) {
        const anyLie = allLies(this.board).length > 0;
        const anyDoubt = allLies(this.board).some((l) =>
          isDoubtPresence(l.presence),
        );
        return {
          ok: false,
          reason: anyLie
            ? anyDoubt
              ? `${def.reference}: no ready lie — peel Doubt outers to Faith, or chip other lies once.`
              : `${def.reference}: no answered lie to redeem — chip one level first.`
            : `${def.reference}: no lie to redeem.`,
        };
      }
      if (!commit) return { ok: true, reason: "" };

      const name = def.convertPrayerName ?? "Redeemed";
      // Doubt cores sit at intensity I after peel; Redeem cards treat them as
      // base II so strong Redeem (e.g. Gen 50:20 +1) can yield Prayer III.
      const lieLv = isDoubtPresence(target.presence)
        ? Math.max(2, target.presence.level)
        : target.presence.level;
      const lieType = target.presence.lieType;
      const { level: lv, elite } = redeemPrayerLevel(def, lieLv);
      const was = target.presence.name;
      this.stats.liesSilenced++;
      this.stats.prayersPlaced++;
      const prayer = makeRedeemPrayer(def, target.slot, name, lv, elite);
      this.board[target.layer].slots[target.slot] = prayer;
      // Stronger redeem pushes Truth harder; elite (III→III) gets a bit more
      const tiltPush = -10 - lv * 2 - (elite ? 4 : 0);
      this.board[target.layer].tilt = clampTilt(
        this.board[target.layer].tilt + tiltPush,
      );
      this.board[target.layer].flash = 0.55;
      const redeemAt = this.originOf(target.layer, target.slot);
      this.log(
        `${was} (${levelRoman(lieLv)}) falls silent · ${def.reference} · ` +
          `${name} (${levelRoman(lv)}${elite ? " fortified" : ""}) abides in ${LAYER_LABELS[target.layer]}`,
        redeemAt,
      );
      this.emitAudio({
        type: "redeem",
        verseId: def.id,
        lieType,
        level: lieLv,
      });
      this.emitBoardFx({
        type: "redeem",
        layer: target.layer,
        slot: target.slot,
      });
      return { ok: true, reason: "" };
    }

    if (sel === "slowLie") {
      const target = this.pickLieTarget(def, intent);
      if (!target) {
        return { ok: false, reason: `${def.reference}: no lie to still.` };
      }
      if (!commit) return { ok: true, reason: "" };

      const at = this.originOf(target.layer, target.slot);
      target.presence.homeSlot = target.slot;
      this.log(`${def.reference} → ${target.presence.name}`, at);
      const dur = def.slowDuration ?? 5;
      const fac = def.slowFactor ?? 0.35;
      target.presence.slowTimer = Math.max(target.presence.slowTimer, dur);
      target.presence.slowFactor = Math.min(target.presence.slowFactor, fac);
      target.presence.advanceProgress *= 0.4;
      target.presence.advanceTimer *= 0.4;
      if (rewindAccusation(target.presence)) {
        this.log(`${target.presence.name} accusation interrupted.`, at);
      }
      const withAnswer = !!def.answerLevels;
      this.emitAudio({
        type: "instant.still",
        verseId: def.id,
        layer: target.layer,
        lieType: target.presence.lieType,
        duration: dur,
        withAnswer,
      });
      this.emitBoardFx({
        type: "still",
        layer: target.layer,
        slot: target.slot,
      });
      if (def.answerLevels) {
        // Same as Game: no correctTarget bonus on Still-on-lie path
        const empowerBonus = this.takeEmpowerBonus(def);
        if (isDogmaPresence(target.presence)) {
          this.resolveDogmaHit(target.presence, target.layer, def, {
            kind: def.nameLie ? "nameAnswer" : "answer",
            logAt: at,
          });
        } else if (isDoubtPresence(target.presence)) {
          this.resolveDoubtHit(target.presence, target.layer, def, {
            kind: "direct",
            empowerBonus,
            logAt: at,
          });
        } else {
          let steps = answerStepCount(def, target.presence.lieType);
          if (empowerBonus > 0) {
            steps = Math.min(3, steps + empowerBonus);
          }
          const meet = this.meetFields(def, target.presence.lieType);
          this.emitAudio({
            type: "answer.resolve",
            verseId: def.id,
            steps,
            meet,
            correctTarget: false,
            lieType: target.presence.lieType,
          });
          this.emitBoardFx({
            type: "strike",
            layer: target.layer,
            slot: target.slot,
            quality: strikeQuality(steps, meet, false),
            steps,
          });
          if (steps > 0) {
            this.stepPresence(target.presence, target.layer, steps, {
              touch: verseTouch(def),
              forceSteps: steps,
              fromVerse: def,
            });
            this.flushSteps();
          }
        }
      } else {
        this.log(
          `${def.reference}: Still on ${target.presence.name} (${dur.toFixed(0)}s).`,
          at,
        );
      }
      return { ok: true, reason: "" };
    }

    // Targeted Answer
    const target = this.pickLieTarget(def, intent);
    if (!target) {
      return {
        ok: false,
        reason: `${def.reference}: no lie to answer.`,
      };
    }

    if (!commit) return { ok: true, reason: "" };

    const at = this.originOf(target.layer, target.slot);
    target.presence.homeSlot = target.slot;
    const correct = isCorrectSelectorTarget(def, target, this.board);
    const empowerBonus = this.takeEmpowerBonus(def);
    const correctName = correct ? correctTargetLabel(def) : null;

    if (def.tiltSlam) {
      this.board[target.layer].tilt = clampTilt(
        this.board[target.layer].tilt + def.tiltSlam,
      );
    }

    if (isDogmaPresence(target.presence)) {
      this.log(
        `${def.reference} → ${target.presence.name} (${LAYER_LABELS[target.layer]})` +
          (correctName ? ` · ${correctName}` : "") +
          (empowerBonus ? " · Empowered" : ""),
        at,
      );
      this.resolveDogmaHit(target.presence, target.layer, def, {
        kind: def.nameLie ? "nameAnswer" : "answer",
        logAt: at,
      });
    } else if (isDoubtPresence(target.presence)) {
      this.log(
        `${def.reference} → ${target.presence.name} (${LAYER_LABELS[target.layer]})` +
          (correctName ? ` · ${correctName}` : "") +
          (empowerBonus ? " · Empowered" : ""),
        at,
      );
      this.resolveDoubtHit(target.presence, target.layer, def, {
        kind: "direct",
        correctTarget: !!correct,
        empowerBonus,
        logAt: at,
      });
    } else {
      let steps = answerStepCount(def, target.presence.lieType, correct);
      if (empowerBonus > 0) {
        steps = Math.min(3, steps + empowerBonus);
      }
      this.log(
        `${def.reference} → ${target.presence.name} (${LAYER_LABELS[target.layer]})` +
          (correctName ? ` · ${correctName}` : "") +
          (empowerBonus ? " · Empowered" : "") +
          (steps <= 0 ? " · no effect" : ""),
        at,
      );

      const meet = this.meetFields(def, target.presence.lieType);
      this.emitAudio({
        type: "answer.resolve",
        verseId: def.id,
        steps,
        meet,
        correctTarget: !!correct,
        lieType: target.presence.lieType,
      });
      this.emitBoardFx({
        type: "strike",
        layer: target.layer,
        slot: target.slot,
        quality: strikeQuality(steps, meet, !!correct),
        steps,
      });

      if (steps > 0) {
        this.stepPresence(target.presence, target.layer, steps, {
          touch: verseTouch(def),
          forceSteps: steps,
          fromVerse: def,
        });
        this.flushSteps();
      } else if (rewindAccusation(target.presence)) {
        this.log(`${target.presence.name} accusation interrupted.`, at);
      }
    }

    // Optional Still rider on the answered lie (if it remains)
    if (
      def.slowDuration &&
      def.slowDuration > 0 &&
      target.presence.level > 0
    ) {
      const dur = def.slowDuration;
      const fac = def.slowFactor ?? 0.35;
      target.presence.slowTimer = Math.max(target.presence.slowTimer, dur);
      target.presence.slowFactor = Math.min(target.presence.slowFactor, fac);
      target.presence.advanceProgress *= 0.4;
      target.presence.advanceTimer *= 0.4;
      if (rewindAccusation(target.presence)) {
        this.log(`${target.presence.name} accusation interrupted.`, at);
      }
      this.log(
        `${def.reference}: Still on ${target.presence.name} (${dur.toFixed(0)}s).`,
        at,
      );
      this.emitAudio({
        type: "instant.still",
        verseId: def.id,
        layer: target.layer,
        lieType: target.presence.lieType,
        duration: dur,
        withAnswer: true,
      });
      this.emitBoardFx({
        type: "still",
        layer: target.layer,
        slot: target.slot,
      });
    }

    return { ok: true, reason: "" };
  }

  /**
   * Step a presence down by `steps` levels (or apply wear if forceSteps not set).
   * For instant Answers, use forceSteps.
   */
  private stepPresence(
    p: Presence,
    layer: LayerId,
    steps: number,
    opts: {
      touch?: TouchAttribution;
      forceSteps?: number;
      skipProclaim?: boolean;
      fromVerse?: VerseDef;
    } = {},
  ): void {
    if (p.level <= 0) return;
    // Doubt / Dogma never classic step-downs
    if (isDoubtPresence(p) || isDogmaPresence(p)) {
      if (opts.touch) p.lastTouch = opts.touch;
      return;
    }
    if (opts.touch && p.side === "enemy") {
      p.lastTouch = opts.touch;
    }
    if (
      p.side === "enemy" &&
      (opts.fromVerse || opts.touch?.kind === "verse") &&
      rewindAccusation(p)
    ) {
      const at = this.originOf(layer, p.homeSlot);
      this.log(`${p.name} accusation interrupted.`, at);
    }

    const n = opts.forceSteps ?? steps;
    const fromLevel = p.level;
    const toLevel = Math.max(0, p.level - n);
    const slot = p.homeSlot;

    if (p.side === "enemy") {
      const def = lieById(p.defId);
      if (!def) {
        p.level = 0;
        return;
      }
      if (toLevel <= 0) {
        p.level = 0;
        this.pendingSteps.push({
          presence: p,
          layer,
          slot,
          fromLevel,
          toLevel: 0,
        });
      } else {
        applyLieStage(p, def, toLevel);
        this.pendingSteps.push({ presence: p, layer, slot, fromLevel, toLevel });
      }
    } else {
      // Prayer: reduce level; strip verbs slightly at residual
      if (toLevel <= 0) {
        p.level = 0;
        this.pendingSteps.push({
          presence: p,
          layer,
          slot,
          fromLevel,
          toLevel: 0,
        });
      } else {
        p.level = toLevel;
        p.wear = 0;
        p.meta = `${p.category ?? "prayer"} · ${levelRoman(toLevel)}`;
        if (toLevel === 1 && p.verbs.contest) {
          p.verbs = {
            ...p.verbs,
            contest: Math.max(0.4, (p.verbs.contest ?? 1) * 0.45),
            pressure: p.verbs.pressure
              ? p.verbs.pressure * 0.5
              : p.verbs.pressure,
          };
        } else if (toLevel === 2 && p.maxLevel >= 3) {
          p.verbs = {
            ...p.verbs,
            contest: p.verbs.contest
              ? Math.max(0.6, p.verbs.contest * 0.7)
              : p.verbs.contest,
            pressure: p.verbs.pressure
              ? p.verbs.pressure * 0.75
              : p.verbs.pressure,
          };
        }
        this.pendingSteps.push({ presence: p, layer, slot, fromLevel, toLevel });
      }
    }

    if (opts.skipProclaim) return;
    // Instant answers get dialog after flush; store verse for silence truth
    if (opts.fromVerse && p.side === "enemy") {
      p.lastTouch = verseTouch(opts.fromVerse);
    }
  }

  /** Apply continuous wear; returns true if a step fired. */
  private applyWear(
    defender: Presence,
    layer: LayerId,
    amount: number,
    touch?: TouchAttribution,
  ): boolean {
    if (defender.level <= 0 || amount <= 0) return false;
    // Doubt is persuaded, not worn down by grind / Joy fields
    if (isDoubtPresence(defender)) {
      if (touch) defender.lastTouch = touch;
      return false;
    }
    if (touch && defender.side === "enemy") defender.lastTouch = touch;
    if (
      defender.side === "enemy" &&
      touch?.kind === "verse" &&
      rewindAccusation(defender)
    ) {
      this.log(
        `${defender.name} accusation interrupted.`,
        this.originOf(layer, defender.homeSlot),
      );
    }
    let amt = amount;
    if (
      defender.side === "player" &&
      defender.fortifyTimer > 0 &&
      defender.fortifyWearFactor < 1
    ) {
      amt *= Math.max(0.15, defender.fortifyWearFactor);
    }
    defender.wear += amt;
    if (defender.wear < 1) return false;
    defender.wear = 0;
    this.stepPresence(defender, layer, 1, {
      touch,
      forceSteps: 1,
      skipProclaim: true,
    });
    return true;
  }

  private flushSteps(): void {
    if (this.pendingSteps.length === 0) {
      this.cleanupDead();
      return;
    }

    // Group by layer for combined mutual logs
    const byLayer = new Map<LayerId, PendingStep[]>();
    for (const s of this.pendingSteps) {
      const list = byLayer.get(s.layer) ?? [];
      list.push(s);
      byLayer.set(s.layer, list);
    }
    this.pendingSteps = [];

    for (const [layer, steps] of byLayer) {
      const lieSteps = steps.filter((s) => s.presence.side === "enemy");
      const prayerSteps = steps.filter((s) => s.presence.side === "player");

      // Combined mutual strain line when both sides step on same layer
      if (lieSteps.length && prayerSteps.length) {
        const L = lieSteps[0]!;
        const P = prayerSteps[0]!;
        const lieBit =
          L.toLevel <= 0
            ? `${L.presence.name} falls silent`
            : `${L.presence.name} (${levelRoman(L.fromLevel)} → ${levelRoman(L.toLevel)})`;
        const prayBit =
          P.toLevel <= 0
            ? `${P.presence.name} breaks`
            : `${P.presence.name} (${levelRoman(P.fromLevel)} → ${levelRoman(P.toLevel)})`;
        for (const s of steps) this.noteLevelStep(s);
        const clashAt = this.originOf(layer, L.slot ?? P.slot);
        this.log(
          `${lieBit} and ${prayBit} strain each other on the ${LAYER_LABELS[layer]}.`,
          clashAt,
        );
        this.emitBoardFx({
          type: "strain",
          layer,
          slot: L.slot ?? P.slot,
        });
        // Extra claim / silence for remaining lie-only extras
        for (const s of lieSteps) {
          const at = this.originOf(s.layer, s.slot);
          if (s.toLevel <= 0) {
            this.emitAudio({
              type: "lie.silence",
              lieId: s.presence.defId,
              lieType: s.presence.lieType ?? "unknown",
              layer: s.layer,
            });
            this.emitBoardFx({
              type: "silence",
              layer: s.layer,
              slot: s.slot,
              side: "enemy",
            });
            this.proclaimTruthOnSilence(s.presence, at);
          } else if (s.toLevel < s.fromLevel) {
            this.emitLieDegrade(s.presence, s.layer);
            this.emitBoardFx({
              type: "degrade",
              layer: s.layer,
              slot: s.slot,
              side: "enemy",
              fromLevel: s.fromLevel,
              toLevel: s.toLevel,
            });
            if (s !== L) {
              this.log(
                `${s.presence.name} (${levelRoman(s.fromLevel)} → ${levelRoman(s.toLevel)})`,
                at,
              );
            }
          }
        }
        for (const s of prayerSteps) {
          if (s.toLevel <= 0) {
            this.emitBoardFx({
              type: "silence",
              layer: s.layer,
              slot: s.slot,
              side: "player",
            });
            if (s !== P) {
              this.log(
                `${s.presence.name} is no longer held.`,
                this.originOf(s.layer, s.slot),
              );
            }
          } else if (s.toLevel < s.fromLevel) {
            this.emitBoardFx({
              type: "degrade",
              layer: s.layer,
              slot: s.slot,
              side: "player",
              fromLevel: s.fromLevel,
              toLevel: s.toLevel,
            });
          }
        }
        continue;
      }

      for (const s of lieSteps) {
        this.noteLevelStep(s);
        const at = this.originOf(s.layer, s.slot);
        if (s.toLevel <= 0) {
          this.log(
            `${s.presence.name} fell silent on the ${LAYER_LABELS[layer]}.`,
            at,
          );
          this.emitAudio({
            type: "lie.silence",
            lieId: s.presence.defId,
            lieType: s.presence.lieType ?? "unknown",
            layer: s.layer,
          });
          this.emitBoardFx({
            type: "silence",
            layer: s.layer,
            slot: s.slot,
            side: "enemy",
          });
          this.proclaimTruthOnSilence(s.presence, at);
        } else {
          if (s.toLevel < s.fromLevel) {
            this.emitLieDegrade(s.presence, s.layer);
            this.emitBoardFx({
              type: "degrade",
              layer: s.layer,
              slot: s.slot,
              side: "enemy",
              fromLevel: s.fromLevel,
              toLevel: s.toLevel,
            });
          }
          this.log(
            `${s.presence.name} (${levelRoman(s.fromLevel)} → ${levelRoman(s.toLevel)}) on ${LAYER_LABELS[layer]}`,
            at,
          );
        }
      }
      for (const s of prayerSteps) {
        this.noteLevelStep(s);
        const at = this.originOf(s.layer, s.slot);
        if (s.toLevel <= 0) {
          this.emitBoardFx({
            type: "silence",
            layer: s.layer,
            slot: s.slot,
            side: "player",
          });
          this.log(
            `${s.presence.name} is no longer held on the ${LAYER_LABELS[layer]}.`,
            at,
          );
        } else {
          this.log(
            `${s.presence.name} strained (${levelRoman(s.fromLevel)} → ${levelRoman(s.toLevel)}) on the ${LAYER_LABELS[layer]}.`,
            at,
          );
        }
      }
    }

    this.cleanupDead();
  }

  private noteLevelStep(s: PendingStep): void {
    if (s.presence.side === "enemy") {
      if (s.toLevel <= 0) this.stats.liesSilenced++;
      else if (s.toLevel < s.fromLevel) this.stats.lieLevelDrops++;
    } else if (s.toLevel <= 0) {
      this.stats.prayersBroken++;
    }
  }

  private proclaimTruthOnSilence(
    lie: Presence,
    origin?: LogOrigin,
  ): void {
    const at = origin ?? this.originOf("mind", lie.homeSlot);
    const touch = lie.lastTouch;
    if (touch) {
      this.log(`Truth spoken: ${touch.reference}`, at);
      return;
    }
    const fb =
      TRUTH_FALLBACK[lie.lieType ?? "fear"] ?? TRUTH_FALLBACK.fear;
    this.log(`Truth spoken: ${fb.reference}`, at);
  }

  private allPlayerPrayers(): {
    presence: Presence;
    layer: LayerId;
    slot: number;
  }[] {
    const out: { presence: Presence; layer: LayerId; slot: number }[] = [];
    for (const layer of LAYER_ORDER) {
      this.board[layer].slots.forEach((p, slot) => {
        if (p?.side === "player" && p.level > 0) {
          out.push({ presence: p, layer, slot });
        }
      });
    }
    return out;
  }

  /** Prayers below maxLevel (worn by contest). */
  private pickDamagedPrayer(
    intent: DragIntent | null,
  ): { presence: Presence; layer: LayerId; slot: number } | null {
    let pool = this.allPlayerPrayers().filter(
      (x) => x.presence.level < x.presence.maxLevel,
    );
    if (!pool.length) return null;

    if (intent?.presenceId) {
      const byId = findPresence(this.board, intent.presenceId);
      if (
        byId &&
        byId.presence.side === "player" &&
        byId.presence.level > 0 &&
        byId.presence.level < byId.presence.maxLevel
      ) {
        return byId;
      }
    }

    if (intent) {
      const exact = pool.find(
        (x) => x.layer === intent.layer && x.slot === intent.slot,
      );
      if (exact) return exact;
      const inLayer = pool.filter((x) => x.layer === intent.layer);
      if (inLayer.length) pool = inLayer;
    }

    // Most damaged first (lowest level, then highest wear)
    pool.sort(
      (a, b) =>
        a.presence.level - b.presence.level ||
        b.presence.wear - a.presence.wear,
    );
    return pool[0] ?? null;
  }

  /**
   * Host for Gather: below max, and at least one other residual I exists.
   * Prefer exact drag target.
   */
  private pickGatherHost(
    intent: DragIntent | null,
  ): PrayerRef | null {
    const all = this.allPlayerPrayers();
    const residualI = all.filter((x) => x.presence.level === 1);

    const canHost = (x: PrayerRef): boolean => {
      if (gatherCapacity(x.presence) <= 0) return false;
      const otherI =
        residualI.length - (x.presence.level === 1 ? 1 : 0);
      return otherI >= 1;
    };

    let pool = all.filter(canHost);
    if (!pool.length) return null;

    if (intent?.presenceId) {
      const byId = findPresence(this.board, intent.presenceId);
      if (byId && byId.presence.side === "player" && canHost(byId)) {
        return byId;
      }
    }

    if (intent) {
      const exact = pool.find(
        (x) => x.layer === intent.layer && x.slot === intent.slot,
      );
      if (exact) return exact;
      const inLayer = pool.filter((x) => x.layer === intent.layer);
      if (inLayer.length) pool = inLayer;
    }

    // Prefer host closest to Will (inner), then most damaged
    pool.sort((a, b) => {
      const ai = LAYER_ORDER.indexOf(a.layer);
      const bi = LAYER_ORDER.indexOf(b.layer);
      if (ai !== bi) return bi - ai;
      return (
        a.presence.level - b.presence.level ||
        b.presence.wear - a.presence.wear
      );
    });
    return pool[0] ?? null;
  }

  /**
   * Raise or set a Prayer to `level` (≤ maxLevel), restoring verbs for that intensity.
   */
  private setPrayerLevel(p: Presence, level: number): void {
    const to = Math.max(1, Math.min(p.maxLevel, Math.round(level)));
    if (to >= p.maxLevel) {
      this.restorePrayerToMax(p);
      return;
    }

    p.level = to;
    p.wear = 0;
    const vdef = verseById(p.defId);
    const elite = /\bfortified\b/i.test(p.meta);
    const redeemed = /\bRedeemed\b/i.test(p.meta);

    let baseVerbs: Presence["verbs"];
    if (vdef?.placeable && !redeemed) {
      baseVerbs = { ...vdef.placeable.verbs };
      p.claim = prayerClaimLine(vdef);
      p.meta = `${vdef.category} · ${levelRoman(to)}`;
    } else if (vdef && redeemed) {
      baseVerbs = redeemPrayerVerbs(p.maxLevel, elite);
      p.meta =
        `${vdef.category} · Redeemed · ${levelRoman(to)}` +
        (elite ? " · fortified" : "");
    } else {
      baseVerbs = { ...p.verbs };
      p.meta = `${p.category ?? "prayer"} · ${levelRoman(to)}`;
    }

    // Mirror stepPresence residual weakening so mid-levels stay consistent
    if (to === 1) {
      p.verbs = {
        ...baseVerbs,
        contest: baseVerbs.contest
          ? Math.max(0.4, baseVerbs.contest * 0.45)
          : baseVerbs.contest,
        pressure: baseVerbs.pressure
          ? baseVerbs.pressure * 0.5
          : baseVerbs.pressure,
      };
    } else if (to === 2 && p.maxLevel >= 3) {
      p.verbs = {
        ...baseVerbs,
        contest: baseVerbs.contest
          ? Math.max(0.6, baseVerbs.contest * 0.7)
          : baseVerbs.contest,
        pressure: baseVerbs.pressure
          ? baseVerbs.pressure * 0.75
          : baseVerbs.pressure,
      };
    } else {
      p.verbs = baseVerbs;
    }
    p.anim = "spawn";
    p.animTime = 0.35;
  }

  /** Full intensity + original verbs after wear step-downs. */
  private restorePrayerToMax(p: Presence): void {
    const max = p.maxLevel;
    p.level = max;
    p.wear = 0;
    const vdef = verseById(p.defId);
    const elite = /\bfortified\b/i.test(p.meta);
    const redeemed = /\bRedeemed\b/i.test(p.meta);

    if (vdef?.placeable && !redeemed) {
      p.verbs = { ...vdef.placeable.verbs };
      p.meta = `${vdef.category} · ${levelRoman(max)}`;
      p.claim = prayerClaimLine(vdef);
    } else if (vdef && redeemed) {
      p.verbs = redeemPrayerVerbs(max, elite);
      p.meta =
        `${vdef.category} · Redeemed · ${levelRoman(max)}` +
        (elite ? " · fortified" : "");
    } else {
      p.meta = `${p.category ?? "prayer"} · ${levelRoman(max)}`;
    }
    p.anim = "spawn";
    p.animTime = 0.35;
  }

  private pickLieTarget(
    def: VerseDef,
    intent: DragIntent | null,
    filter?: (p: Presence) => boolean,
  ): { presence: Presence; layer: LayerId; slot: number } | null {
    let lies = allLies(this.board);
    if (filter) lies = lies.filter((l) => filter(l.presence));
    if (lies.length === 0) return null;

    // Sticky aim: follow the presence the player dropped on, even if it moved
    if (intent?.presenceId) {
      const byId = findPresence(this.board, intent.presenceId);
      if (
        byId &&
        byId.presence.side === "enemy" &&
        byId.presence.level > 0 &&
        (!filter || filter(byId.presence))
      ) {
        return byId;
      }
    }

    if (intent) {
      const exact = lies.find(
        (l) => l.layer === intent.layer && l.slot === intent.slot,
      );
      if (exact) return exact;

      const mask = def.respectMask;
      if (
        mask === "layerPrefer" ||
        mask === "layerOnly" ||
        mask === "exactSlot"
      ) {
        const inLayer = lies.filter((l) => l.layer === intent.layer);
        if (inLayer.length) lies = inLayer;
      }
    }

    const sel = def.selector ?? "strongestLie";
    const byLevel = (
      a: { presence: Presence; layer: LayerId },
      b: { presence: Presence; layer: LayerId },
    ) => {
      const dl = b.presence.level - a.presence.level;
      if (dl !== 0) return dl;
      return (
        LAYER_ORDER.indexOf(b.layer) - LAYER_ORDER.indexOf(a.layer)
      );
    };

    if (sel === "innermostLie") {
      lies.sort((a, b) => {
        const di = LAYER_ORDER.indexOf(b.layer) - LAYER_ORDER.indexOf(a.layer);
        if (di !== 0) return di;
        return b.presence.level - a.presence.level;
      });
      return lies[0] ?? null;
    }

    if (sel === "typePriority" && def.priorityTypes?.length) {
      for (const t of def.priorityTypes) {
        const pool = lies.filter((l) => l.presence.lieType === t);
        if (pool.length) {
          pool.sort(byLevel);
          return pool[0]!;
        }
      }
    }

    lies.sort(byLevel);
    return lies[0] ?? null;
  }

  private cleanupDead(): void {
    for (const id of LAYER_ORDER) {
      const slots = this.board[id].slots;
      for (let i = 0; i < slots.length; i++) {
        const p = slots[i];
        if (p && p.level <= 0) {
          slots[i] = null;
        }
      }
    }
  }

  private stableEmptySlot(layer: LayerId, prefer = 1): number | null {
    const order = [prefer, 1, 0, 2].filter(
      (v, i, a) => v >= 0 && v <= 2 && a.indexOf(v) === i,
    );
    for (const s of order) {
      if (
        !this.board[layer].slots[s] &&
        canPlaceInSlot(this.board, layer, s, "enemy")
      ) {
        return s;
      }
    }
    return null;
  }

  private weakestPlayerOn(
    layer: LayerId,
  ): { presence: Presence; slot: number } | null {
    const foes = this.board[layer].slots
      .map((p, i) => ({ p, i }))
      .filter((o) => o.p?.side === "player") as {
      p: Presence;
      i: number;
    }[];
    if (!foes.length) return null;
    foes.sort((a, b) => a.p.level - b.p.level || b.p.wear - a.p.wear);
    return { presence: foes[0]!.p, slot: foes[0]!.i };
  }

  /**
   * Clash when a lie tries to push into a prayer’s layer/slot.
   * Mutual wear — the lie does not free-step the keep.
   * @param attackerLayer layer the lie currently occupies (often one outward)
   * @returns whether the prayer broke (lie may advance into the slot)
   */
  private siegePrayer(
    prayer: Presence,
    layer: LayerId,
    attacker: Presence,
    attackerLayer: LayerId,
  ): boolean {
    // Base clash cost ≈ half a level each; higher level hits harder, Guard resists.
    let wearOnPrayer = 0.42 + attacker.level * 0.1;
    let wearOnLie = 0.42 + prayer.level * 0.1;

    const diff = attacker.level - prayer.level;
    if (diff > 0) {
      wearOnPrayer *= 1 + diff * 0.2;
      wearOnLie *= Math.max(0.45, 1 - diff * 0.2);
    } else if (diff < 0) {
      wearOnLie *= 1 + -diff * 0.2;
      wearOnPrayer *= Math.max(0.45, 1 + diff * 0.2);
    }

    if (prayer.verbs.guard) wearOnPrayer *= 0.65;
    // Contested keep pushes back harder
    if (prayer.verbs.contest) {
      wearOnLie *= 1 + Math.min(0.5, prayer.verbs.contest * 0.12);
    }
    if (prayer.category && meetsLie(prayer.category, attacker.lieType)) {
      wearOnLie *= 1.15;
    }

    // Cap so a single push never free-kills either side from full wear alone
    wearOnPrayer = Math.min(0.92, wearOnPrayer);
    wearOnLie = Math.min(0.92, wearOnLie);

    const touch = prayerTouch(prayer);
    this.applyWear(prayer, layer, wearOnPrayer);
    this.applyWear(attacker, attackerLayer, wearOnLie, touch);
    this.flushSteps();

    return prayer.level <= 0;
  }

  private siegeSpawnInto(
    def: LieDef,
    layer: LayerId,
    skin?: LieSpawnSkin,
    siegeFirst = false,
  ): "placed" | "damaged" | "none" {
    const target = this.weakestPlayerOn(layer);
    if (!target) return "none";

    this.board[layer].tilt = clampTilt(this.board[layer].tilt + 10);
    this.board[layer].flash = 0.5;

    // Incoming claim strains the wall — wear, not a free full step-down.
    // If the keep holds, the lie still enters weakened (paid the clash).
    const prayer = target.presence;
    const fromPrayer = prayer.level;
    let wearOnPrayer = siegeFirst ? 0.9 : 0.7;
    if (prayer.verbs.guard) wearOnPrayer *= 0.65;
    this.applyWear(prayer, layer, wearOnPrayer);
    this.flushSteps();

    if (prayer.level <= 0) {
      this.board[layer].slots[target.slot] = makeLiePresence(def, 0, {
        homeSlot: target.slot,
        anim: "arrive",
        skin,
        dogmaScaffold:
          def.type === "dogma" ? this.pickDogmaScaffold() : undefined,
      });
      const lie = this.board[layer].slots[target.slot]!;
      this.stats.liesSpawned++;
      this.emitDogmaNotice(lie);
      this.log(
        `${lie.name} breaches the ${LAYER_LABELS[layer]} (breaks the keep): “${lie.claim}”`,
        this.originOf(layer, target.slot),
      );
      this.emitAudio({ type: "lie.breach", layer });
      this.emitLieSpawn(lie, layer);
      return "placed";
    }

    // Wall holds: lie enters at reduced intensity (clash cost)
    const maxLv = lieMaxLevel(def);
    const entryLevel = Math.max(1, maxLv - 1);
    const lie = makeLiePresence(def, 0, {
      homeSlot: target.slot,
      level: entryLevel,
      anim: "arrive",
      skin,
      dogmaScaffold:
        def.type === "dogma" ? this.pickDogmaScaffold() : undefined,
    });
    // Prefer an empty slot if any opened; else don't displace surviving prayer
    const empty = this.stableEmptySlot(layer, target.slot);
    if (empty !== null) {
      this.board[layer].slots[empty] = lie;
      this.stats.liesSpawned++;
      this.log(
        `${lie.name} enters the ${LAYER_LABELS[layer]} strained (${levelRoman(entryLevel)}): “${lie.claim}”` +
          (fromPrayer !== prayer.level
            ? ` · ${prayer.name} (${levelRoman(fromPrayer)} → ${levelRoman(prayer.level)})`
            : ` · ${prayer.name} holds`),
        this.originOf(layer, empty),
      );
      this.emitLieSpawn(lie, layer);
      return "placed";
    }

    this.log(
      `${lie.name} hammers the ${LAYER_LABELS[layer]} wall — ${prayer.name} holds` +
        (fromPrayer !== prayer.level
          ? ` (${levelRoman(fromPrayer)} → ${levelRoman(prayer.level)})`
          : ` (wear ${Math.round(prayer.wear * 100)}%)`) +
        ".",
      this.originOf(layer, target.slot),
    );
    return "damaged";
  }

  private lieSiegeFirst(def: LieDef): boolean {
    return !!lieStageAt(def, lieMaxLevel(def)).verbs.siegeFirst;
  }

  private pickDogmaScaffold(explicit?: DogmaScaffold): DogmaScaffold {
    if (explicit) return explicit;
    const n = this.deckPool.filter((id) => WISDOM_VERSE_IDS.includes(id)).length;
    return n >= 2 ? "full" : "teach";
  }

  private emitDogmaNotice(p: Presence): void {
    if (this.dogmaNoticed || !p.dogma) return;
    this.dogmaNoticed = true;
    this.dogmaNotice = {
      demand: dogmaDemand(p.dogma),
      wisdomInDeck: this.deckPool.some((id) => WISDOM_VERSE_IDS.includes(id)),
      remaining: dogmaRemaining(p.dogma),
      core: dogmaIsCore(p.dogma),
      named: p.dogma.named,
      sequence: [...p.dogma.sequence],
      scaffold: p.dogma.scaffold,
    };
  }

  /**
   * Settled enters a column on Mind: wipe that top tile (Prayer or lie),
   * then sit down. No clash, no other-layer fallback.
   */
  private pickDogmaColumn(): number {
    const mind = this.board.mind.slots;
    const order = [1, 0, 2];
    for (const s of order) {
      const occ = mind[s];
      if (occ && occ.level > 0) return s;
    }
    return 1;
  }

  private wipeColumnTop(slot: number): Presence | null {
    return this.wipeLaneOccupant(
      "mind",
      slot,
      `Settled takes the column — swept from the Mind.`,
    );
  }

  /** Silence whoever occupies this cell (Prayer or lie). */
  private wipeLaneOccupant(
    layer: LayerId,
    slot: number,
    logLine?: string,
  ): Presence | null {
    const p = this.board[layer].slots[slot];
    if (!p || p.level <= 0) return null;
    const was = p.name;
    const side = p.side;
    p.level = 0;
    this.board[layer].slots[slot] = null;
    this.board[layer].flash = 0.7;
    if (side === "enemy") this.stats.liesSilenced++;
    if (logLine !== "") {
      this.log(
        logLine
          ? `${was} — ${logLine}`
          : `${was} is swept from the ${LAYER_LABELS[layer]}.`,
        this.originOf(layer, slot),
      );
    }
    this.emitBoardFx({
      type: "silence",
      layer,
      slot,
      side,
    });
    this.emitAudio({
      type: "lie.silence",
      lieId: p.instanceId,
      lieType: p.lieType ?? (side === "player" ? "prayer" : "fear"),
      layer,
    });
    return p;
  }

  /** Shove this layer past the lie-advance gate so Settled does not stall. */
  private leanLayerForDogma(layer: LayerId): void {
    const band = tiltBand(layer);
    const floor = band.lieStrong + 14;
    if (this.board[layer].tilt < floor) {
      this.board[layer].tilt = clampTilt(floor);
    }
  }

  private trySpawnDogma(
    def: LieDef,
    skin?: LieSpawnSkin,
    dogmaScaffold?: DogmaScaffold,
  ): boolean {
    if (countOnBoard(this.board, def.id) >= (def.maxOnBoard ?? 1)) return false;
    const slot = this.pickDogmaColumn();
    this.wipeColumnTop(slot);
    const lie = makeLiePresence(def, 0, {
      homeSlot: slot,
      skin,
      dogmaScaffold: this.pickDogmaScaffold(dogmaScaffold),
    });
    this.board.mind.slots[slot] = lie;
    this.leanLayerForDogma("mind");
    this.stats.liesSpawned++;
    this.emitDogmaNotice(lie);
    const shells = lie.dogma
      ? `${lie.dogma.scaffold} ${lie.dogma.index + 1}/${lie.dogma.sequence.length}`
      : "";
    this.log(
      `${lie.name} settles on the Mind: “${lie.claim}”` +
        (shells ? ` · ${shells}` : ""),
      this.originOf("mind", slot),
    );
    this.emitLieSpawn(lie, "mind");
    this.emitBoardFx({
      type: "boss.arrive",
      layer: "mind",
      slot,
      name: lie.name,
      claim: lie.claim,
    });
    return true;
  }

  private trySpawnLie(
    lieId: string,
    layer: LayerId,
    skin?: LieSpawnSkin,
    doubtScaffold?: DoubtScaffold,
    opts?: { siegeFirst?: boolean; dogmaScaffold?: DogmaScaffold },
  ): boolean {
    const def = lieById(lieId);
    if (!def) return false;
    if (def.type === "dogma") {
      return this.trySpawnDogma(def, skin, opts?.dogmaScaffold);
    }
    const max = def.maxOnBoard ?? 4;
    if (countOnBoard(this.board, lieId) >= max) return false;

    const tryLayers: LayerId[] = [layer];
    for (const L of LAYER_ORDER) {
      if (!tryLayers.includes(L)) tryLayers.push(L);
    }

    const siegeFirst = !!(opts?.siegeFirst || this.lieSiegeFirst(def));

    // Keep-hunters: hammer a Prayer on the intended layer before filling a gap
    // elsewhere (that path is how prayer blankets turn into spawn-and-die).
    // If the wall holds, still try to land in a leftover slot so the hunter
    // stays on the board (Answer / Restore have something to do).
    if (siegeFirst) {
      for (const L of tryLayers) {
        if (!layerHasSide(this.board, L, "player")) continue;
        const result = this.siegeSpawnInto(def, L, skin, true);
        if (result === "placed") return true;
        if (result === "damaged") break;
      }
    }

    for (const L of tryLayers) {
      if (totalPresences(this.board) >= BOARD_CAP) break;
      const slot = this.stableEmptySlot(L, 1);
      if (slot === null) continue;
      const lie = makeLiePresence(def, 0, {
        homeSlot: slot,
        skin,
        doubtScaffold:
          def.type === "doubt" ? (doubtScaffold ?? "full") : undefined,
      });
      this.board[L].slots[slot] = lie;
      this.stats.liesSpawned++;
      const scaffoldBit =
        def.type === "doubt" && lie.doubt?.outerCount === 0
          ? " · core open (Answer or Redeem)"
          : def.type === "doubt" && lie.doubt
            ? ` · ${lie.doubt.outerFaith}/${lie.doubt.outerCount + 1} Faith`
            : "";
      this.log(
        `${lie.name} enters the ${LAYER_LABELS[L]}: “${lie.claim}”${scaffoldBit}`,
        this.originOf(L, slot),
      );
      this.emitLieSpawn(lie, L);
      return true;
    }

    for (const L of tryLayers) {
      const result = this.siegeSpawnInto(def, L, skin, siegeFirst);
      if (result === "placed" || result === "damaged") return result === "placed";
    }
    return false;
  }

  private tickDirector(): void {
    for (let i = 0; i < this.scenario.spawns.length; i++) {
      if (this.firedSpawns.has(i)) continue;
      const ev = this.scenario.spawns[i]!;
      if (this.elapsed < ev.at) continue;
      this.firedSpawns.add(i);
      if (ev.label) this.waveLabel = ev.label;
      const layer = ev.layer ?? "mind";
      const wallUp =
        this.isEndless() &&
        countSide(this.board, "player") >= PRAYER_WALL_COUNT;
      const spawnLayer = wallUp
        ? layerWithMostPrayers(this.board, layer)
        : layer;
      this.trySpawnLie(ev.lieId, spawnLayer, ev.skin, ev.doubtScaffold, {
        siegeFirst: wallUp,
        dogmaScaffold: ev.dogmaScaffold,
      });
    }
    if (this.isEndless() && this.scenario.escalation) {
      this.tickEscalation();
    }
  }

  private beginJoyField(def: VerseDef): void {
    const jf = def.joyField;
    if (!jf) return;
    const duration = Math.max(0.5, jf.duration);
    const wearPerSec = Math.max(0.02, jf.wearPerSec);
    const onlyTypes: LieType[] =
      jf.onlyTypes && jf.onlyTypes.length ? [...jf.onlyTypes] : ["fear"];
    const slowFactor = Math.min(1, Math.max(0.2, jf.slowFactor ?? 0.65));
    const maxStepsPerLie = Math.max(1, Math.min(2, jf.maxStepsPerLie ?? 1));

    this.joyField = {
      remaining: duration,
      wearPerSec,
      onlyTypes,
      slowFactor,
      maxStepsPerLie,
      stepsDone: new Map(),
      reference: def.reference,
      defId: def.id,
    };

    // Soft Still on current matching lies (refresh if re-cast)
    let n = 0;
    for (const { presence: p } of allLies(this.board)) {
      if (p.side !== "enemy" || p.level <= 0) continue;
      if (p.lieType && onlyTypes.includes(p.lieType)) {
        p.slowTimer = Math.max(p.slowTimer, duration);
        p.slowFactor = Math.min(p.slowFactor, slowFactor);
        n++;
      }
    }

    const typeLabel = onlyTypes
      .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
      .join("/");
    this.log(
      `${def.reference}: Joy of the Lord · ${duration.toFixed(0)}s · ` +
        `slowly contests ${typeLabel} lies` +
        (n ? ` (${n} present)` : " (none present yet)") +
        ` · up to ${maxStepsPerLie} step${maxStepsPerLie === 1 ? "" : "s"} each.`,
      this.originOf("will", 1),
    );
  }

  /**
   * Soft board-wide contest: wear matching lie types over time.
   * Caps steps per lie so multi-target Joy can't clear a board.
   */
  private tickJoyField(dt: number): void {
    const field = this.joyField;
    if (!field) return;

    field.remaining -= dt;
    const touch: TouchAttribution = {
      kind: "verse",
      defId: field.defId,
      reference: field.reference,
      line: shortScripture("…the joy of the Lord is your strength."),
    };

    for (const { presence: p, layer, slot } of allLies(this.board)) {
      if (p.side !== "enemy" || p.level <= 0) continue;
      if (!p.lieType || !field.onlyTypes.includes(p.lieType)) continue;

      // Keep mild Still refreshed while Joy lasts
      if (field.remaining > 0) {
        p.slowTimer = Math.max(p.slowTimer, Math.min(0.35, field.remaining));
        p.slowFactor = Math.min(p.slowFactor, field.slowFactor);
      }

      const done = field.stepsDone.get(p.instanceId) ?? 0;
      if (done >= field.maxStepsPerLie) continue;

      let amt = field.wearPerSec * dt;
      if (p.verbs.guard) amt *= 0.65;
      // Residual (I) wears a bit faster so Joy can finish chips
      if (p.level <= 1) amt *= 1.15;

      const fromLevel = p.level;
      const name = p.name;
      const stepped = this.applyWear(p, layer, amt, touch);
      if (stepped) {
        field.stepsDone.set(p.instanceId, done + 1);
        // applyWear uses skipProclaim — surface one quiet joy line
        this.log(
          `${field.reference}: Joy wears ${name} (${levelRoman(fromLevel)}→${levelRoman(Math.max(0, p.level))}).`,
          this.originOf(layer, slot),
        );
      }
    }

    if (field.remaining <= 0) {
      this.joyField = null;
    }
  }

  /**
   * After the scripted open, rotate named adversary strategies and keep spawning
   * with slowly rising density (shorter intervals each full cycle).
   */
  private tickEscalation(): void {
    const config = this.scenario.escalation;
    if (!config?.strategies.length) return;

    const scriptEnd = scriptEndTime(this.scenario.spawns);
    const leadIn = config.leadIn ?? 3;
    const startAt = scriptEnd + leadIn;
    if (this.elapsed < startAt) return;

    if (!this.escalation.active) {
      this.escalation.active = true;
      this.escalation.startAt = startAt;
      this.escalation.strategyIndex = 0;
      this.escalation.strategyStartedAt = this.elapsed;
      this.escalation.lastSpawnAt = this.elapsed - 1;
      this.escalation.cycles = 0;
      const first = currentStrategy(config, this.escalation);
      if (first) {
        this.waveLabel = first.label;
        this.log(`${first.label} begins.`, "meta");
      }
      // Open free play only — muster has no soft-resume checkpoints
      if (!this.isMusterScenario()) {
        this.maybeEmitProtocolCheckpoint(true);
      }
      this.emitProtocolBoundary(true);
    }

    if (maybeAdvanceStrategy(config, this.escalation, this.elapsed)) {
      const next = currentStrategy(config, this.escalation);
      if (next) {
        this.waveLabel =
          this.escalation.cycles > 0
            ? `${next.label} · cycle ${this.escalation.cycles + 1}`
            : next.label;
        this.log(
          `${this.waveLabel}${this.escalation.cycles > 0 ? " (harder)" : ""}.`,
          "meta",
        );
      }
      if (!this.isMusterScenario()) {
        this.maybeEmitProtocolCheckpoint(true);
      }
      this.emitProtocolBoundary(false);
    }

    const strat = currentStrategy(config, this.escalation);
    if (!strat) return;

    const interval = effectiveInterval(
      strat,
      this.escalation.cycles,
      this.escalationIntervalMul(),
    );
    if (this.elapsed - this.escalation.lastSpawnAt < interval) return;
    this.escalation.lastSpawnAt = this.elapsed;

    const choice = pickWeighted(strat.pool);
    if (!choice) return;

    const wallUp = countSide(this.board, "player") >= PRAYER_WALL_COUNT;
    const hunt = wallUp || !!choice.huntPrayers;
    const preferred = layerOf(choice);
    const spawnLayer = hunt
      ? layerWithMostPrayers(this.board, preferred)
      : preferred;

    this.trySpawnLie(
      choice.lieId,
      spawnLayer,
      undefined,
      choice.doubtScaffold,
      { siegeFirst: wallUp, dogmaScaffold: choice.dogmaScaffold },
    );
  }

  private setAnim(p: Presence, anim: Presence["anim"], time = 0.4): void {
    p.anim = anim;
    p.animTime = time;
  }

  private afterLayerArrival(p: Presence): void {
    p.advanceTimer = 0;
    p.advanceProgress = 0;
    p.dwell = ADVANCE_DWELL;
    p.marching = false;
    this.setAnim(p, "arrive", 0.45);
  }

  private leaveDogmaEcho(
    layer: LayerId,
    slot: number,
    parent: Presence,
  ): void {
    if (this.board[layer].slots[slot]) return;
    if (totalPresences(this.board) >= BOARD_CAP) return;
    const conf = lieById("confusion");
    if (!conf) return;
    if (countOnBoard(this.board, "confusion") >= (conf.maxOnBoard ?? 4)) return;
    const echo = makeLiePresence(conf, 0, {
      homeSlot: slot,
      level: 1,
      anim: "multiply",
    });
    echo.sourceLieId = parent.defId;
    echo.claim = confusionClaim(parent.defId, 1);
    echo.name = "Tangled Echo";
    this.board[layer].slots[slot] = echo;
    this.stats.liesSpawned++;
    this.log(
      `${parent.name} leaves a twisted echo on the ${LAYER_LABELS[layer]}: “${echo.claim}”`,
      this.originOf(layer, slot),
    );
    this.emitLieSpawn(echo, layer, { multiplied: true });
  }

  private settleDogmaOnWill(p: Presence): void {
    if (!isDogmaPresence(p)) return;
    p.verbs = {
      ...p.verbs,
      taxAfter: p.verbs.taxAfter ?? 3.4,
      taxHit: p.verbs.taxHit ?? 26,
    };
    p.taxTimer = 0;
    p.taxProgress = 0;
    this.leanLayerForDogma("will");
    this.log(
      `${p.name} settles on the Will: “${p.claim}” — the argument becomes how you live.`,
      this.originOf("will", p.homeSlot),
    );
    this.emitBoardFx({
      type: "boss.settle",
      layer: "will",
      slot: p.homeSlot,
      name: p.name,
      claim: p.claim,
    });
  }

  /**
   * Player Prayer advances outward (toward Mind). Fragile path: empty preferred;
   * enemy in lane is a costly clash (no free breaches).
   */
  private pushPlayerPrayer(
    p: Presence,
    from: LayerId,
    fromSlot: number,
    to: LayerId,
  ): "moved" | "sieged" | "blocked" {
    const lane = p.homeSlot >= 0 && p.homeSlot <= 2 ? p.homeSlot : fromSlot;
    const destSlots = this.board[to].slots;

    if (!destSlots[lane] && canPlaceInSlot(this.board, to, lane, "player")) {
      this.board[from].slots[fromSlot] = null;
      destSlots[lane] = p;
      p.homeSlot = lane;
      this.afterLayerArrival(p);
      this.board[to].flash = 0.45;
      this.board[to].tilt = clampTilt(this.board[to].tilt - 5);
      this.log(
        `${p.name} walks the path into the ${LAYER_LABELS[to]}.`,
        this.originOf(to, lane),
      );
      return "moved";
    }

    const laneOcc = destSlots[lane];
    if (laneOcc?.side === "enemy") {
      this.setAnim(p, "siege", 0.35);
      this.board[to].flash = 0.35;
      // Clash: path is fragile (no Guard) — prayer takes the worse half
      let wearOnPrayer = 0.48 + laneOcc.level * 0.12;
      let wearOnLie = 0.38 + p.level * 0.1;
      if (p.category && meetsLie(p.category, laneOcc.lieType)) {
        wearOnLie *= 1.2;
      }
      if (!p.verbs.guard) wearOnPrayer *= 1.15;
      wearOnPrayer = Math.min(0.95, wearOnPrayer);
      wearOnLie = Math.min(0.9, wearOnLie);
      this.applyWear(p, from, wearOnPrayer);
      this.applyWear(laneOcc, to, wearOnLie, prayerTouch(p));
      this.flushSteps();
      this.board[to].tilt = clampTilt(this.board[to].tilt - 4);

      if (p.level <= 0) {
        this.board[from].slots[fromSlot] = null;
        this.cleanupDead();
        this.log(
          `${p.name} falters on the path into the ${LAYER_LABELS[to]}.`,
          this.originOf(from, fromSlot),
        );
        return "sieged";
      }
      if (laneOcc.level <= 0) {
        destSlots[lane] = null;
        this.board[from].slots[fromSlot] = null;
        destSlots[lane] = p;
        p.homeSlot = lane;
        this.afterLayerArrival(p);
        this.board[to].flash = 0.55;
        this.log(
          `${p.name} clears the way into the ${LAYER_LABELS[to]}.`,
          this.originOf(to, lane),
        );
        return "moved";
      }
      p.advanceTimer = 0;
      p.advanceProgress = 0;
      return "sieged";
    }

    if (laneOcc?.side === "player") {
      // Don't displace a keep — try another column
      const alt = this.stableEmptySlot(to, lane);
      if (alt !== null && canPlaceInSlot(this.board, to, alt, "player")) {
        this.board[from].slots[fromSlot] = null;
        destSlots[alt] = p;
        p.homeSlot = alt;
        this.afterLayerArrival(p);
        this.board[to].flash = 0.4;
        this.log(
          `${p.name} walks the path into the ${LAYER_LABELS[to]}.`,
          this.originOf(to, alt),
        );
        return "moved";
      }
      p.advanceTimer = 0;
      p.advanceProgress = 0;
      return "blocked";
    }

    const alt = this.stableEmptySlot(to, lane);
    if (alt !== null && canPlaceInSlot(this.board, to, alt, "player")) {
      this.board[from].slots[fromSlot] = null;
      destSlots[alt] = p;
      p.homeSlot = alt;
      this.afterLayerArrival(p);
      this.board[to].flash = 0.45;
      this.board[to].tilt = clampTilt(this.board[to].tilt - 5);
      this.log(
        `${p.name} walks the path into the ${LAYER_LABELS[to]}.`,
        this.originOf(to, alt),
      );
      return "moved";
    }

    p.advanceTimer = 0;
    p.advanceProgress = 0;
    return "blocked";
  }

  /**
   * Settled keeps its column: wipe the destination cell (any side) and walk in.
   */
  private pushDogmaWalkOver(
    p: Presence,
    from: LayerId,
    fromSlot: number,
    to: LayerId,
  ): "moved" {
    const lane = p.homeSlot >= 0 && p.homeSlot <= 2 ? p.homeSlot : fromSlot;
    const destSlots = this.board[to].slots;
    const occ = destSlots[lane];
    if (occ && occ.instanceId !== p.instanceId) {
      const was = occ.name;
      this.wipeLaneOccupant(to, lane, "");
      this.log(
        `${p.name} walks over ${was} into the ${LAYER_LABELS[to]}.`,
        this.originOf(to, lane),
      );
    }
    this.board[from].slots[fromSlot] = null;
    destSlots[lane] = p;
    p.homeSlot = lane;
    this.afterLayerArrival(p);
    this.leanLayerForDogma(to);
    this.board[to].flash = 0.7;
    this.log(
      `${p.name} walks the column into the ${LAYER_LABELS[to]}: “${p.claim}”`,
      this.originOf(to, lane),
    );
    this.emitAudio({ type: "lie.breach", layer: to });
    return "moved";
  }

  private pushOrSiege(
    p: Presence,
    from: LayerId,
    fromSlot: number,
    to: LayerId,
  ): "moved" | "sieged" | "blocked" {
    if (isDogmaPresence(p)) {
      return this.pushDogmaWalkOver(p, from, fromSlot, to);
    }
    const lane = p.homeSlot >= 0 && p.homeSlot <= 2 ? p.homeSlot : fromSlot;
    const destSlots = this.board[to].slots;

    if (!destSlots[lane] && canPlaceInSlot(this.board, to, lane, "enemy")) {
      this.board[from].slots[fromSlot] = null;
      destSlots[lane] = p;
      p.homeSlot = lane;
      this.afterLayerArrival(p);
      this.board[to].flash = 0.55;
      this.log(
        `${p.name} presses into the ${LAYER_LABELS[to]}: “${p.claim}”`,
        this.originOf(to, lane),
      );
      this.emitAudio({ type: "lie.press", layer: to });
      return "moved";
    }

    const laneOcc = destSlots[lane];
    if (laneOcc?.side === "player") {
      this.setAnim(p, "siege", 0.35);
      this.board[to].flash = 0.4;
      this.board[to].tilt = clampTilt(this.board[to].tilt + 6);
      laneOcc.homeSlot = lane;
      const broke = this.siegePrayer(laneOcc, to, p, from);
      // Lie may have been stepped/silenced in the clash
      if (p.level <= 0) {
        this.board[from].slots[fromSlot] = null;
        this.cleanupDead();
        return "sieged";
      }
      if (broke || laneOcc.level <= 0) {
        destSlots[lane] = null;
        this.board[from].slots[fromSlot] = null;
        destSlots[lane] = p;
        p.homeSlot = lane;
        this.afterLayerArrival(p);
        this.board[to].flash = 0.65;
        this.log(
          `${p.name} breaches the ${LAYER_LABELS[to]}: “${p.claim}”`,
          this.originOf(to, lane),
        );
        this.emitAudio({ type: "lie.breach", layer: to });
        return "moved";
      }
      return "sieged";
    }

    const alt = this.stableEmptySlot(to, lane);
    if (alt !== null) {
      this.board[from].slots[fromSlot] = null;
      destSlots[alt] = p;
      p.homeSlot = alt;
      this.afterLayerArrival(p);
      this.board[to].flash = 0.55;
      this.log(
        `${p.name} presses into the ${LAYER_LABELS[to]}: “${p.claim}”`,
        this.originOf(to, alt),
      );
      this.emitAudio({ type: "lie.press", layer: to });
      return "moved";
    }

    const defenders = destSlots
      .map((x, i) => ({ x, i }))
      .filter((o) => o.x?.side === "player") as { x: Presence; i: number }[];
    if (defenders.length === 0) return "blocked";

    defenders.sort((a, b) => a.x.level - b.x.level || b.x.wear - a.x.wear);
    const target = defenders[0]!;
    this.setAnim(p, "siege", 0.35);
    this.board[to].flash = 0.4;
    this.board[to].tilt = clampTilt(this.board[to].tilt + 6);
    target.x.homeSlot = target.i;
    const broke = this.siegePrayer(target.x, to, p, from);
    if (p.level <= 0) {
      this.board[from].slots[fromSlot] = null;
      this.cleanupDead();
      return "sieged";
    }
    if (broke || target.x.level <= 0) {
      destSlots[target.i] = null;
      this.board[from].slots[fromSlot] = null;
      destSlots[target.i] = p;
      p.homeSlot = target.i;
      this.afterLayerArrival(p);
      this.board[to].flash = 0.65;
      this.log(
        `${p.name} breaches the ${LAYER_LABELS[to]}: “${p.claim}”`,
        this.originOf(to, target.i),
      );
      this.emitAudio({ type: "lie.breach", layer: to });
      return "moved";
    }
    return "sieged";
  }

  /**
   * Residual (I) claim twists into Confusion (II): keeps a thread of the old
   * lie, dresses it in something that sounds good but doesn't hold.
   * Weak to every Answer type.
   */
  private morphToConfusion(
    p: Presence,
    layer: LayerId,
    slot: number,
  ): void {
    if (p.side !== "enemy" || p.level !== 1) return;
    if (p.lieType === "confusion" || p.defId === "confusion") return;
    if (p.lieType === "doubt" || p.doubt) return;
    if (p.lieType === "dogma" || p.dogma) return;
    const conf = lieById("confusion");
    if (!conf) return;
    if (countOnBoard(this.board, "confusion") >= (conf.maxOnBoard ?? 4)) return;

    const wasName = p.name;
    const sourceId = p.defId;
    p.sourceLieId = sourceId;
    p.defId = "confusion";
    p.lieType = "confusion";
    p.maxLevel = lieMaxLevel(conf);
    p.confuseTimer = 0;
    p.wear = 0;
    p.advanceTimer = 0;
    p.advanceProgress = 0;
    applyLieStage(p, conf, 2);
    this.setAnim(p, "spawn", 0.5);
    this.board[layer].flash = 0.55;
    this.board[layer].tilt = clampTilt(this.board[layer].tilt + 5);
    const at = this.originOf(layer, slot);
    this.log(
      `${wasName} twists into Confusion on the ${LAYER_LABELS[layer]}: “${p.claim}”`,
      at,
    );
    this.emitLieSpawn(p, layer, { multiplied: true });
  }

  private tryMultiply(parent: Presence, layer: LayerId, slot: number): void {
    if (totalPresences(this.board) >= BOARD_CAP) return;
    // Confusion does not multiply — it is a rebound of a residual
    if (parent.defId === "confusion" || parent.lieType === "confusion") return;
    // Doubt does not multiply — one question, not a swarm
    if (parent.lieType === "doubt" || parent.doubt) return;
    if (parent.lieType === "dogma" || parent.dogma) return;
    const def = lieById(parent.defId);
    if (!def) return;
    if (countOnBoard(this.board, def.id) >= (def.maxOnBoard ?? 4)) return;

    const childLevel = Math.max(1, parent.level - 1);
    const sideOrder = [slot - 1, slot + 1, 0, 1, 2].filter(
      (v, i, a) => v >= 0 && v <= 2 && v !== slot && a.indexOf(v) === i,
    );
    for (const s of sideOrder) {
      if (!this.board[layer].slots[s] && canPlaceInSlot(this.board, layer, s, "enemy")) {
        const child = makeLiePresence(def, parent.spreadGen + 1, {
          marching: true,
          anim: "multiply",
          homeSlot: s,
          level: childLevel,
          inheritFrom: parent,
        });
        this.board[layer].slots[s] = child;
        this.stats.liesSpawned++;
        this.log(
          `${parent.name} multiplies beside itself in the ${LAYER_LABELS[layer]}: “${child.claim}”`,
          this.originOf(layer, s),
        );
        this.emitLieSpawn(child, layer, { multiplied: true });
        return;
      }
    }

    const inn = layerInward(layer);
    if (inn && this.board[inn].tilt >= tiltBand(inn).lieMarch) {
      const lane = parent.homeSlot;
      if (!this.board[inn].slots[lane] && canPlaceInSlot(this.board, inn, lane, "enemy")) {
        const child = makeLiePresence(def, parent.spreadGen + 1, {
          marching: true,
          anim: "multiply",
          homeSlot: lane,
          level: childLevel,
          inheritFrom: parent,
        });
        child.dwell = ADVANCE_DWELL;
        this.board[inn].slots[lane] = child;
        this.board[inn].flash = 0.4;
        this.stats.liesSpawned++;
        this.log(
          `${parent.name} multiplies into the corrupted ${LAYER_LABELS[inn]}: “${child.claim}”`,
          this.originOf(inn, lane),
        );
        this.emitLieSpawn(child, inn, { multiplied: true });
        return;
      }

      const front = this.board[inn].slots[lane];
      if (front?.side === "player") {
        this.setAnim(parent, "siege", 0.35);
        this.board[inn].flash = 0.45;
        this.board[inn].tilt = clampTilt(this.board[inn].tilt + 5);
        const broke = this.siegePrayer(front, inn, parent, layer);
        if (parent.level <= 0) {
          // Parent spent itself multiplying into the keep
          return;
        }
        if (broke || front.level <= 0) {
          const child = makeLiePresence(def, parent.spreadGen + 1, {
            anim: "arrive",
            homeSlot: lane,
            level: childLevel,
            inheritFrom: parent,
          });
          child.dwell = ADVANCE_DWELL;
          child.marching = false;
          this.board[inn].slots[lane] = child;
          this.stats.liesSpawned++;
          this.log(
            `${parent.name} multiplies through the keep into the ${LAYER_LABELS[inn]}: “${child.claim}”`,
            this.originOf(inn, lane),
          );
          this.emitAudio({ type: "lie.breach", layer: inn });
          this.emitLieSpawn(child, inn, { multiplied: true });
        }
      }
    }
  }

  private tickPresences(dt: number): void {
    type Adv = {
      from: LayerId;
      slot: number;
      to: LayerId;
      opportunistic: boolean;
      side: "enemy" | "player";
    };
    const advances: Adv[] = [];
    const spreads: { layer: LayerId; slot: number; presence: Presence }[] = [];
    const confusions: { layer: LayerId; slot: number; presence: Presence }[] =
      [];

    // --- Mutual contest wear (each presence wears one foe on target layer) ---
    // Default: same layer. Path prayers may contest the next layer ahead instead.
    // Incoming wear on a single Prayer is soft-capped: N lies must not N× shred it.
    type WearHit = {
      foe: Presence;
      amount: number;
      touch?: ReturnType<typeof prayerTouch>;
    };
    const incomingOnPlayer = new Map<string, WearHit[]>();

    for (const id of LAYER_ORDER) {
      const layer = this.board[id];
      for (let slot = 0; slot < layer.slots.length; slot++) {
        const p = layer.slots[slot];
        if (!p || p.level <= 0 || effectiveContest(p) <= 0) continue;

        // contestNextLayer: wear foes on the layer in advance direction
        // (player outward toward Mind; enemy inward toward Secret Place).
        let foeLayerId: LayerId = id;
        if (p.verbs.contestNextLayer) {
          const next =
            p.side === "player" ? layerOutward(id) : layerInward(id);
          if (!next) continue;
          foeLayerId = next;
        }
        const foeLayer = this.board[foeLayerId];

        const foes = foeLayer.slots
          .map((x, i) => ({ x, i }))
          .filter(
            (o) =>
              o.x &&
              o.x.side !== p.side &&
              o.x.level > 0 &&
              slotsAdjacent(slot, o.i),
          ) as {
          x: Presence;
          i: number;
        }[];
        if (!foes.length) continue;

        // Prefer same column, then highest level foe (already adjacent)
        foes.sort((a, b) => {
          const colA = Math.abs(a.i - slot);
          const colB = Math.abs(b.i - slot);
          if (colA !== colB) return colA - colB;
          return b.x.level - a.x.level;
        });
        const foe = foes[0]!.x;
        const rate = wearRate(p, foe);
        if (rate <= 0) continue;
        const amount = rate * dt;
        const touch =
          p.side === "player" ? prayerTouch(p) : undefined;

        if (foe.side === "player") {
          // Defer lie→prayer wear so multi-attackers don't N× shred the keep
          const list = incomingOnPlayer.get(foe.instanceId) ?? [];
          list.push({ foe, amount });
          incomingOnPlayer.set(foe.instanceId, list);
        } else {
          this.applyWear(foe, foeLayerId, amount, touch);
        }
      }
    }

    // Apply lie→prayer wear with diminishing returns (1 + 0.45 + 0.25 + …)
    for (const [prayerId, hits] of incomingOnPlayer) {
      hits.sort((a, b) => b.amount - a.amount);
      let total = 0;
      for (let i = 0; i < hits.length; i++) {
        const falloff = i === 0 ? 1 : i === 1 ? 0.45 : i === 2 ? 0.25 : 0.12;
        total += hits[i]!.amount * falloff;
      }
      // Find prayer + layer
      let found: { p: Presence; layer: LayerId } | null = null;
      for (const id of LAYER_ORDER) {
        for (const p of this.board[id].slots) {
          if (p?.instanceId === prayerId) {
            found = { p, layer: id };
            break;
          }
        }
        if (found) break;
      }
      if (found) this.applyWear(found.p, found.layer, total);
    }
    this.flushSteps();

    for (const id of LAYER_ORDER) {
      const layer = this.board[id];
      for (let slot = 0; slot < layer.slots.length; slot++) {
        const p = layer.slots[slot];
        if (!p) continue;
        p.age += dt;
        if (p.animTime > 0) {
          p.animTime -= dt;
          if (p.animTime <= 0) {
            p.animTime = 0;
            if (p.anim !== "push") p.anim = null;
          }
        }

        if (p.verbs.pressure) {
          let rate = p.verbs.pressure;
          if (p.verbs.guard) rate *= 0.85;
          // Weaker residual presence
          rate *= 0.55 + p.level * 0.15;
          layer.tilt = clampTilt(layer.tilt + rate * dt);
        }
        if (isDogmaPresence(p)) {
          const floor = tiltBand(id).lieStrong + 10;
          if (layer.tilt < floor) {
            layer.tilt = clampTilt(layer.tilt + 28 * dt);
          }
        }

        if (p.dwell > 0) {
          p.dwell = Math.max(0, p.dwell - dt);
        }
        if (p.slowTimer > 0) {
          p.slowTimer = Math.max(0, p.slowTimer - dt);
          if (p.slowTimer <= 0) p.slowFactor = 1;
        }
        if (p.fortifyTimer > 0) {
          p.fortifyTimer = Math.max(0, p.fortifyTimer - dt);
          if (p.fortifyTimer <= 0) p.fortifyWearFactor = 1;
        }

        p.homeSlot = slot;
        const settled = p.dwell <= 0;

        // --- Player Prayer advance (outward toward Mind) ---
        if (p.side === "player" && p.verbs.advanceAfter) {
          const uncontested = isUncontested(this.board, id, "player");
          const tilt = layer.tilt;
          const truthReady = tilt <= tiltBand(id).prayerAdvance;
          // Contested path still crawls if the layer is light enough
          const mayAdvance =
            settled &&
            !!p.verbs.advanceAfter &&
            (uncontested || truthReady);
          const outward = layerOutward(id);

          if (mayAdvance && outward && p.advanceProgress >= 0.92) {
            const lane = p.homeSlot;
            const front = this.board[outward].slots[lane];
            if (!front && canPlaceInSlot(this.board, outward, lane, "player")) {
              advances.push({
                from: id,
                slot,
                to: outward,
                opportunistic: true,
                side: "player",
              });
              continue;
            }
          }

          if (mayAdvance && outward) {
            // Contested = fragile path slows hard (weak self-protection)
            const rate = uncontested ? 1 : 0.42;
            const speechMul = this.isProclaiming() ? 0.78 : 1;
            p.advanceTimer += dt * rate * speechMul;
            const need = p.verbs.advanceAfter ?? 5;
            p.advanceProgress = Math.min(1, p.advanceTimer / need);
            if (p.advanceProgress > 0.12) {
              p.anim = "push";
              p.animTime = Math.max(p.animTime, 0.15);
            }
            if (p.advanceTimer >= need) {
              advances.push({
                from: id,
                slot,
                to: outward,
                opportunistic: false,
                side: "player",
              });
              p.advanceTimer = 0;
            }
          } else if (p.verbs.advanceAfter) {
            if (!settled || (!uncontested && !truthReady)) {
              p.advanceTimer = Math.max(0, p.advanceTimer - dt * 0.4);
              p.advanceProgress = Math.max(0, p.advanceProgress - dt * 0.4);
              if (p.advanceProgress < 0.08 && p.anim === "push") p.anim = null;
            }
          }
          continue;
        }

        if (p.side !== "enemy") continue;

        const uncontested = isUncontested(this.board, id, "enemy");
        const enemyPacked = this.board[id].slots.every((s) => s?.side === "enemy");
        const tilt = layer.tilt;
        const band = tiltBand(id);
        const tiltReady = p.marching
          ? tilt >= band.lieMarch
          : tilt >= band.lieStrong;
        const mayAdvance =
          settled &&
          tiltReady &&
          !!p.verbs.advanceAfter &&
          (p.marching || uncontested || enemyPacked || tilt >= band.lieStrong);
        const inward = layerInward(id);

        if (mayAdvance && inward && p.advanceProgress >= 0.92) {
          const lane = p.homeSlot;
          const front = this.board[inward].slots[lane];
          if (isDogmaPresence(p)) {
            advances.push({
              from: id,
              slot,
              to: inward,
              opportunistic: true,
              side: "enemy",
            });
            continue;
          }
          if (!front && canPlaceInSlot(this.board, inward, lane, "enemy")) {
            advances.push({
              from: id,
              slot,
              to: inward,
              opportunistic: true,
              side: "enemy",
            });
            continue;
          }
        }

        if (mayAdvance && inward) {
          const rate = enemyPacked && !uncontested && !p.marching ? 0.85 : 1;
          const slowMul = p.slowTimer > 0 ? p.slowFactor : 1;
          // Mild slow while proclaiming (not a hard freeze)
          const speechMul = this.isProclaiming() ? 0.72 : 1;
          p.advanceTimer +=
            dt * rate * slowMul * speechMul * this.enemyPaceMul();
          const need = p.verbs.advanceAfter ?? 3;
          p.advanceProgress = Math.min(1, p.advanceTimer / need);
          if (p.advanceProgress > 0.12) {
            p.anim = "push";
            p.animTime = Math.max(p.animTime, 0.15);
          }
          if (p.advanceTimer >= need) {
            advances.push({
              from: id,
              slot,
              to: inward,
              opportunistic: false,
              side: "enemy",
            });
            p.advanceTimer = 0;
          }
        } else {
          if (!tiltReady || !settled) {
            p.advanceTimer = Math.max(0, p.advanceTimer - dt * 0.35);
            p.advanceProgress = Math.max(0, p.advanceProgress - dt * 0.35);
            if (p.advanceProgress < 0.08 && p.anim === "push") p.anim = null;
          }
        }

        const speechMul = this.isProclaiming() ? 0.72 : 1;

        if (
          p.verbs.spreadAfter &&
          p.spreadGen < 1 &&
          !p.marching &&
          settled &&
          (uncontested || enemyPacked || tilt >= tiltBand(id).lieMarch)
        ) {
          p.spreadTimer += dt * speechMul * this.enemyPaceMul();
          if (p.spreadTimer >= p.verbs.spreadAfter) {
            spreads.push({ layer: id, slot, presence: p });
            p.spreadTimer = 0;
          }
        } else if (p.verbs.spreadAfter) {
          p.spreadTimer = Math.max(0, p.spreadTimer - dt * 0.4);
        }

        // Doubt: incomplete outer Faith re-doubts when timer is enabled (Still pauses)
        if (
          isDoubtPresence(p) &&
          p.doubt &&
          p.doubt.decayEnabled &&
          p.doubt.outerFaith > 0
        ) {
          if (p.slowTimer > 0) {
            // Still holds the question open
          } else {
            p.doubt.decayAcc += dt * speechMul;
            if (p.doubt.decayAcc >= DOUBT_DECAY_SECONDS) {
              const { state, reverted } = applyDoubtDecay(p.doubt);
              p.doubt = state;
              if (reverted) {
                this.syncDoubtPresence(p);
                const prog = doubtFaithProgress(state);
                this.log(
                  `${p.name}: the question returns · ${prog.faith}/${prog.total} Faith`,
                  this.originOf(id, slot),
                );
                this.emitBoardFx({
                  type: "degrade",
                  layer: id,
                  slot,
                  side: "enemy",
                  fromLevel: p.level + 1,
                  toLevel: p.level,
                });
              }
            }
          }
        }

        // Residual (I) lies may twist into Confusion (II) — half-truth rebound
        // (disabled on early teaching scenarios — unfinished chips stay soft)
        // Doubt never morphs — finish the onion or it re-doubts, it does not twist.
        if (
          !this.scenario.disableConfusion &&
          p.level === 1 &&
          p.lieType !== "dogma" &&
          !p.dogma &&
          p.lieType &&
          p.lieType !== "confusion" &&
          p.lieType !== "doubt" &&
          p.defId !== "confusion"
        ) {
          p.confuseTimer = (p.confuseTimer ?? 0) + dt * speechMul;
          if (p.confuseTimer >= CONFUSE_AFTER) {
            confusions.push({ layer: id, slot, presence: p });
            p.confuseTimer = 0;
          }
        } else if (p.level !== 1) {
          p.confuseTimer = 0;
        }
      }
    }

    this.cleanupDead();

    advances.sort((a, b) => Number(b.opportunistic) - Number(a.opportunistic));
    for (const mv of advances) {
      const p = this.board[mv.from].slots[mv.slot];
      if (!p || p.side !== mv.side) continue;
      if (mv.side === "enemy") {
        const result = this.pushOrSiege(p, mv.from, mv.slot, mv.to);
        if (result === "moved" && isDogmaPresence(p)) {
          this.leaveDogmaEcho(mv.from, mv.slot, p);
          if (mv.to === "will") this.settleDogmaOnWill(p);
        }
      } else {
        this.pushPlayerPrayer(p, mv.from, mv.slot, mv.to);
      }
    }

    this.cleanupDead();

    for (const c of confusions) {
      const located =
        this.board[c.layer].slots[c.slot]?.instanceId === c.presence.instanceId
          ? { presence: c.presence, layer: c.layer, slot: c.slot }
          : findPresence(this.board, c.presence.instanceId);
      if (!located) continue;
      this.morphToConfusion(located.presence, located.layer, located.slot);
    }

    this.cleanupDead();

    for (const sp of spreads) {
      const located =
        this.board[sp.layer].slots[sp.slot]?.instanceId === sp.presence.instanceId
          ? { presence: sp.presence, layer: sp.layer, slot: sp.slot }
          : findPresence(this.board, sp.presence.instanceId);
      if (!located) continue;
      this.tryMultiply(located.presence, located.layer, located.slot);
    }

    this.cleanupDead();
  }

  /**
   * A packed row (every slot occupied) frays everyone on it — Prayer or lie,
   * adjacent or not. Contest still handles the extra adjacent grind.
   * Doubt is persuaded, not worn; skip those.
   */
  private tickCrowding(dt: number): void {
    const wearEach = 0.12 * dt;
    for (const id of LAYER_ORDER) {
      const slots = this.board[id].slots;
      const living: { p: Presence; i: number }[] = [];
      for (let i = 0; i < slots.length; i++) {
        const p = slots[i];
        if (p && p.level > 0) living.push({ p, i });
      }
      if (living.length < SLOT_COUNT) {
        this.crowdLogged.delete(id);
        continue;
      }
      if (!this.crowdLogged.has(id)) {
        this.crowdLogged.add(id);
        this.log(
          `${LAYER_LABELS[id]} is packed — the whole row frays.`,
          this.originOf(id, 1),
        );
      }
      for (const { p } of living) {
        if (isDoubtPresence(p) || isDogmaPresence(p)) continue;
        this.applyWear(p, id, wearEach);
      }
    }
  }

  /**
   * Empty Words-style accusation: long visible windup, then a Secret Place
   * hit. Player Answers rewind the charge even if the lie survives.
   */
  private tickAccusation(dt: number): void {
    const pace =
      this.difficulty === "easy"
        ? 0.82
        : this.difficulty === "hard"
          ? 1.12
          : 1;
    const speechMul = this.isProclaiming() ? 0.78 : 1;

    for (const id of LAYER_ORDER) {
      for (let slot = 0; slot < this.board[id].slots.length; slot++) {
        const p = this.board[id].slots[slot];
        if (!p || p.side !== "enemy" || p.level <= 0) continue;
        const need = p.verbs.taxAfter;
        if (!need || need <= 0) {
          p.taxTimer = 0;
          p.taxProgress = 0;
          continue;
        }

        let fill = dt * pace * speechMul;
        if (p.slowTimer > 0) fill *= Math.max(0.2, p.slowFactor);
        p.taxTimer = (p.taxTimer ?? 0) + fill;
        p.taxProgress = Math.min(1, p.taxTimer / need);

        if (p.taxTimer < need) continue;

        const raw = p.verbs.taxHit ?? 12;
        const hit = Math.max(4, Math.round(raw * this.willDpsMul()));
        this.resources.integrity = Math.max(0, this.resources.integrity - hit);
        p.taxTimer = 0;
        p.taxProgress = 0;
        this.board[id].flash = 0.45;
        this.log(
          `${p.name} accuses the Secret Place (−${hit} Integrity).`,
          this.originOf(id, slot),
        );
        this.emitAudio({ type: "lie.breach", layer: "will" });
        this.emitBoardFx({ type: "strain", layer: id, slot });
      }
    }
  }

  private tickMeters(dt: number): void {
    this.board.will.tilt = clampTilt(this.board.will.tilt - 3.2 * dt);

    for (const id of LAYER_ORDER) {
      const layer = this.board[id];
      if (layer.flash > 0) layer.flash = Math.max(0, layer.flash - dt);

      let guard = 0;
      let enemyCount = 0;
      let playerCount = 0;
      for (const p of layer.slots) {
        if (!p) continue;
        if (p.side === "enemy") enemyCount++;
        if (p.side === "player") playerCount++;
        if (p.verbs.guard) guard += p.side === "player" ? 0.75 : 0.35;
      }
      const overflowResist = 1 / (1 + guard * 0.28);
      const packedBonus = enemyCount >= 3 ? 1.45 : enemyCount >= 2 ? 1.15 : 1;

      const band = tiltBand(id);
      if (layer.tilt > band.overflow && enemyCount >= 1) {
        const span = Math.max(20, 100 - band.overflow);
        const force =
          ((layer.tilt - band.overflow) / span) *
          18 *
          overflowResist *
          packedBonus *
          dt;
        const inn = layerInward(id);
        if (inn) {
          this.board[inn].tilt = clampTilt(this.board[inn].tilt + force);
          if (enemyCount >= 3 && layer.tilt > band.overflowCrush) {
            for (const p of this.board[inn].slots) {
              if (p?.side === "player") {
                this.applyWear(p, inn, 0.35 * dt * packedBonus);
              }
            }
          }
          // Discrete overflow tick — only this branch (never generic flash elsewhere)
          if (force > 0.08 && layer.flash <= 0) {
            layer.flash = 0.35;
            this.emitAudio({ type: "overflow.flash", layer: id });
          }
        }
      }

      if (layer.tilt < band.truthOverflow && playerCount >= 1) {
        const span = Math.max(20, 100 + band.truthOverflow);
        const force = ((band.truthOverflow - layer.tilt) / span) * 6 * dt;
        const out = layerOutward(id);
        if (out) {
          this.board[out].tilt = clampTilt(this.board[out].tilt - force);
        }
      }

      if (enemyCount === 0 && playerCount === 0) {
        if (layer.tilt > 2) {
          layer.tilt = clampTilt(layer.tilt - band.settle * dt);
        } else if (layer.tilt < -2) {
          layer.tilt = clampTilt(layer.tilt + band.settle * dt);
        }
      }
    }

    this.flushSteps();
    this.tickCrowding(dt);
    this.flushSteps();

    const willFriendlies = this.board.will.slots.filter(
      (p) => p?.side === "player",
    ).length;
    const willEnemies = this.board.will.slots.filter(
      (p) => p?.side === "enemy",
    ).length;
    const willHeld = willFriendlies > 0;
    let mindEnemiesOpen = 0;
    let heartEnemiesOpen = 0;
    for (const p of this.board.mind.slots) {
      if (p?.side === "enemy") mindEnemiesOpen++;
    }
    for (const p of this.board.heart.slots) {
      if (p?.side === "enemy") heartEnemiesOpen++;
    }

    this.tickAccusation(dt);

    let dogmaOnWill = false;
    for (const p of this.board.will.slots) {
      if (p && isDogmaPresence(p) && p.level > 0) {
        dogmaOnWill = true;
        break;
      }
    }
    if (dogmaOnWill) {
      // Boss bleed: works through a keep. Tax is the big bite; this is the grind.
      let bleed = 8.5;
      if (this.isProclaiming()) bleed *= 0.88;
      bleed *= this.willDpsMul();
      this.resources.integrity -= bleed * dt;
    }

    if (!willHeld && willEnemies > 0) {
      const willTilt = this.board.will.tilt;
      // P3: punish open Will; speech lock only mildly eases (not a free shield)
      let dps = willEnemies * 3.1;
      if (willTilt > 15) {
        dps += ((willTilt - 15) / 85) * 12;
      }
      if (this.isProclaiming()) dps *= 0.88;
      dps *= this.willDpsMul();
      this.resources.integrity -= dps * dt;
    } else if (!willHeld) {
      // Teaching lever: Guard Prayer matters on Mind-only stages.
      // Outer lies with an empty Will erode integrity (softer than a Will
      // breach, but enough that answer-only spam without a keep fails Easy).
      const outer = mindEnemiesOpen + heartEnemiesOpen;
      if (outer >= 1) {
        let dps = 1.15 + outer * 1.35;
        if (this.isProclaiming()) dps *= 0.9;
        dps *= this.willDpsMul();
        this.resources.integrity -= dps * dt;
      }
    }

    // Refuge: Prayers on Will with refugeRegen mend Secret Place integrity
    {
      let regen = 0;
      for (const p of this.board.will.slots) {
        if (p?.side === "player" && p.level > 0 && p.verbs.refugeRegen) {
          regen += p.verbs.refugeRegen * (p.level / Math.max(1, p.maxLevel));
        }
      }
      // Soft cap so stacked Refuge doesn't trivialize DPS
      if (regen > 0) {
        regen = Math.min(4.5, regen);
        this.resources.integrity = Math.min(
          this.resources.maxIntegrity,
          this.resources.integrity + regen * dt,
        );
      }
    }

    if (this.resources.integrity <= 0) {
      this.resources.integrity = 0;
      if (this.status === "playing") {
        this.status = "lost";
        this.log("You retreated to the Secret Place.");
        this.emitAudio({ type: "mission.lost" });
      }
    }
  }

  update(dt: number): void {
    if (this.status !== "playing" || this.paused) return;

    this.elapsed += dt;

    // Speech lock ticks even while board sim runs
    if (this.proclaimLock > 0) {
      this.proclaimLock = Math.max(0, this.proclaimLock - dt);
      if (this.proclaimLockMax > 0 && this.proclaimWordCount > 0) {
        if (this.karaokeDriver) {
          const elapsed = this.proclaimLockMax - this.proclaimLock;
          this.proclaimWordIndex = this.karaokeDriver.wordIndex(
            elapsed,
            this.proclaimLockMax,
            this.proclaimWordCount,
          );
        } else {
          const spoken = 1 - this.proclaimLock / this.proclaimLockMax;
          this.proclaimWordIndex = Math.min(
            this.proclaimWordCount,
            Math.floor(spoken * this.proclaimWordCount + 1e-6),
          );
        }
      }
      if (this.proclaimLock <= 0) {
        // Capture id before clear — audio needs proclaim.end payload
        const endedId = this.proclaimVerseId;
        this.proclaimWordIndex = this.proclaimWordCount;
        if (endedId) {
          this.emitAudio({ type: "proclaim.end", verseId: endedId });
        }
        this.proclaimRef = "";
        this.proclaimText = "";
        this.proclaimLockMax = 0;
        this.proclaimVerseId = "";
      }
    }

    this.stats.minIntegrity = Math.min(
      this.stats.minIntegrity,
      this.resources.integrity,
    );

    this.tickDirector();
    this.tickJoyField(dt);
    this.tickPresences(dt);
    this.tickMeters(dt);

    // tickMeters may set lost; cast breaks TS narrowing from the early "playing" guard
    const st = this.status as GameStatus;

    if (st === "lost") {
      if (!this.endLogged) {
        this.endLogged = true;
        this.finalizeStats();
        if (this.isEndless()) {
          this.log(
            `Survived ${this.elapsed.toFixed(1)}s · lies silenced ${this.stats.liesSilenced}/${this.stats.liesSpawned} · verses ${this.stats.versesPlayed}`,
            "meta",
          );
        } else {
          this.log(
            `Retreated · t=${this.elapsed.toFixed(1)}s · integrity 0 · lies silenced ${this.stats.liesSilenced}/${this.stats.liesSpawned} · verses ${this.stats.versesPlayed}`,
            "meta",
          );
        }
      }
      return;
    }

    // Endless free play never wins on a timer — only integrity 0 ends the run.
    if (this.isEndless()) return;

    if (st === "playing" && this.elapsed >= this.scenario.duration) {
      if (this.resources.integrity > 0) {
        this.status = "won";
        if (!this.endLogged) {
          this.endLogged = true;
          this.finalizeStats();
          this.log("You held Mind, Heart, and Will.");
          this.log(
            `Victory · t=${this.elapsed.toFixed(1)}s · integrity ${Math.round(this.resources.integrity)} · lies silenced ${this.stats.liesSilenced}/${this.stats.liesSpawned} · verses ${this.stats.versesPlayed}`,
            "meta",
          );
          const maxHp = Math.max(1, this.resources.maxIntegrity);
          const nearMiss =
            this.stats.minIntegrity / maxHp < 0.15 ||
            this.resources.integrity / maxHp < 0.15;
          this.emitAudio({ type: "mission.won", nearMiss });
        }
      }
    }
  }
}
